/**
 * The whole HUD, assembled — one screen's worth, at whatever size the screen is.
 *
 * 🔴 **Why this file exists.** `design-hud-nopanel.mjs` drew the cluster over the
 * midday frame, and haugur chose what he saw there. Testing the same design at
 * night needs the identical drawing over a different photograph — and if the
 * night generator kept its own copy of the layout, the two would drift, and the
 * first thing to drift would be the thing he already agreed to. So the scene is
 * here, both generators import it, and **the day picture and the night picture
 * are the same code by construction, not by care.**
 *
 * 🔴 **And why it is in the CLIENT.** Same argument, one step further, and it is
 * the argument haugur chose as option A (T149). The game draws its HUD from this
 * file; the sample sheets in `Design/samples/` are drawn from this file. **A
 * picture he approved cannot stop describing the game without the test in
 * `hud-scene.test.ts` going red.** It moved here on 2026-08-18, and samples
 * 30–40 regenerate byte-for-byte across the move, which is how the move was
 * checked — the same check `hud-parts.ts` passed the same day.
 *
 * ⚠️ **The sample states live in `tools/design/hud-scene.mjs`, not here.** The
 * demonstration inventory, the healthy and freezing states, the crop and the
 * Playwright page are all things only a sheet needs. The game passes its own.
 *
 * Geometry, and why it is not up for discussion in this file: haugur objected to
 * the *drawing*, never to the layout. 48 px slots, 4 px gaps, 516 px across ten,
 * 11 px bars, exposure 300 px and centred — settled in `00_Visual_Language.md`
 * §13.10 and §13.12.
 *
 * ⚠️ **Every row is measured from the BOTTOM of the screen**, which is what lets
 * the same code serve a 1280 × 800 sample sheet and whatever size haugur's
 * window happens to be.
 */

import { makeParts, type Garment, type SlotContent, type StruckOptions } from './hud-parts.ts';

/**
 * 🧵 **What a loom's screen shows — D136.** Words already in the player's
 * language (the drawing never translates), and numbers already worked out.
 */
export interface LoomPicture {
  /** `loom.ts`'s `LoomDoing`. */
  readonly doing: 'idle' | 'warping' | 'weaving' | 'noWeft' | 'clothFull';
  /** How far into the step under way, 0–1, for the bar. */
  readonly part: number;
  /** How much of the piece on the loom is woven, 0–1, for the picture. */
  readonly woven: number;
  /** What it is doing, as a sentence. */
  readonly line: string;
  /** The three slots' names. */
  readonly warp: string;
  readonly weft: string;
  readonly cloth: string;
}

/** How big the screen is, and how big a slot is on it. */
export interface SceneSize {
  /** Screen width in px. The cluster is centred in it. */
  readonly SW?: number;
  /** Screen height in px. ⚠️ The **canvas** height, not the window's — the
   *  world starts below the status bar and so does the HUD. */
  readonly SH?: number;
  readonly SLOT?: number;
  readonly GAP?: number;
  /**
   * 🔴 **How much of THIS drawing the status bar sits over.**
   *
   * ⚠️ **34 in a sample sheet and 0 in the game, and both are right.** A sheet's
   * SVG covers the whole 1280 × 800 window, so the bar is inside it and the
   * pack's dimming has to start below it. The game's overlay is already
   * positioned at `top: var(--topbar-h)`, so the bar is *outside* it and the
   * dimming starts at nothing.
   *
   * 🔴 **This was a real defect, found in the first shot of the deployed
   * build** (2026-08-19, T152). The 34 was written for the sheet and applied
   * twice in the game, leaving **a 34 px band of undimmed world across the top
   * of the screen** — measured at luminance 130 against 66 immediately below
   * it. Nothing threw, every gate was green, and the sample sheets were
   * perfect: **a design sheet is evidence about the design, not about the
   * screen it lands on.**
   */
  readonly TOPBAR?: number;
}

/**
 * 🔍 **The size the whole drawing was judged at, and the reason it has to be
 * written down** (T170).
 *
 * `Design/00_Visual_Language.md` §13.10 chose every number in this file over a
 * **1280 × 800** frame, and recorded what it was choosing: ten slots at 48 px
 * are **516 px, about 40 % of the width — the share Minecraft's nine take at
 * GUI scale 3.** That share is the design; 48 px is only how the share was
 * spelled on that screen.
 *
 * 🔴 **Nothing enforced the share, so it quietly stopped holding.** The px were
 * fixed and the screens were not. On haugur's 2560 × 1440 the same 516 px is
 * **about 18 %** of the width — the drawing exactly as approved, at less than
 * half the size it was approved at. 🎯 **He found it by looking, 2026-08-31:**
 * *"they are small, images are small and text is hard to read."*
 */
const DESIGN_W = 1280;
const DESIGN_H = 800;

/**
 * 🔍 **How much bigger to draw the HUD on this screen** — 1 on the frame it was
 * designed for, more on a larger one.
 *
 * 🎯 **haugur chose this shape, 2026-08-31**, over one fixed size and over a
 * slider: *automatic, from the screen.* 👉 So the HUD covers **the same share of
 * the glass on every monitor**, which is what §13.10 actually decided, and
 * neither of us has to touch a number again when the screen changes.
 *
 * ⚠️ **The whole picture is zoomed — it is NOT redrawn bigger.** Slots, icons,
 * spacing and text all move together because the caller scales the `viewBox`,
 * so nothing in this file or in `hud-parts.ts` changes and **no approved
 * drawing moves**. `hud-scene.test.ts` still hashes the same bytes. 🔴 That is
 * the property to protect: the day a size change starts editing the marks, it
 * needs haugur's eye on a sheet, and this one does not.
 *
 * **The rule: the largest quarter-step that still leaves room for the frame the
 * design was judged on.** Both directions have to fit, so the smaller of the two
 * ratios wins — on a 1440-tall screen it is the *height* that binds, not the
 * width.
 *
 * ⚠️ **Quarter steps rather than whole ones, and this is a departure worth
 * naming.** Minecraft steps by whole numbers because its interface is a bitmap
 * and a fractional zoom blurs it. Ours is SVG and stays crisp at any factor —
 * but whole steps would give **1× on a 1440-tall screen** (2× needs 1600) and
 * change nothing at all, which is the complaint restated rather than answered.
 * 👉 Quarter steps land on **1.75×** there. The reason to keep *steps* at all is
 * that a handful of sizes can each be photographed and looked at; a factor that
 * varies with every window drag cannot.
 *
 * 🔴 **Never below 1.** The design frame is the floor — a smaller screen gets
 * the drawing as approved and no less. On a 1366 × 768 laptop the pack still
 * clears the on-hand row by 36 px, which is what makes the floor safe.
 *
 * @param w Screen width in px — the same width `makeScene` is given.
 * @param h Canvas height in px. ⚠️ **The canvas, not the window** — the status
 *   bar is above it, and counting it would over-estimate the room by its height.
 */
export function hudScale(w: number, h: number): number {
  if (!(w > 0) || !(h > 0)) return 1;
  const fits = Math.min(w / DESIGN_W, h / DESIGN_H);
  const stepped = Math.floor(fits * 4) / 4;
  return Math.min(3, Math.max(1, stepped));
}

/**
 * The four no-panel variations. ✅ **haugur chose `scrim`.**
 *
 * ⚠️ **The three rejected ones are kept deliberately** — a sheet that can no
 * longer draw the option that lost stops being an argument, and `Design/00`
 * §13.11–§13.13 is written against these pictures.
 */
export type VariantName = 'bare' | 'battens' | 'tally' | 'scrim';

/** What the cluster is reading out. */
export interface ClusterState {
  /** 0–100. Below 30 the rod turns to `hverleir` — health in danger. */
  readonly health: number;
  /** 0–100. */
  readonly food: number;
  /**
   * −1 (hard frost) … 0 (comfortable) … +1 (too hot).
   *
   * 🔴 **A number because a needle needs a position — NOT a continuous comfort
   * value.** 🎯 **D84, haugur, 2026-08-21:** shelter is **four discrete rungs**
   * (`shelter.ts`) and this gauge **follows them**, so it takes one of a few
   * fixed positions and snaps between them.
   *
   * ⚠️ **Smoothing it into a free-running value would be the temperature field
   * ADR-0016 §6 bans, wearing a different name** — and its faults would be
   * indistinguishable from weather faults, which on this project means nobody
   * would ever find them (**D2**).
   */
  readonly expo: number;
  /** D64 §7 — the HUD says WHY, in words, and **only when there is a why**. */
  readonly reason?: string | null;
  /** Ten slots. Short arrays and gaps are fine; a missing slot draws empty. */
  readonly contents: readonly SlotContent[];
  /** Which slot is in hand, 0–9. −1 selects none. */
  readonly selected: number;
  /**
   * The controls hint, which lives in the cluster's own reserved row.
   *
   * 🔴 **This is the collision fix, and it is the design's rather than mine.**
   * The game's `.controls` strip sits at `bottom: 12px`, which is **exactly
   * where the on-hand row goes.** Putting the line inside the cluster is what
   * §13.10 already decided; nothing here should rediscover it.
   */
  readonly controls?: string | null;
}

/** 🐑 T286 — how urgent the aim line is: a block or a fed animal, or one in need. */
export type AimTone = 'plain' | 'hungry' | 'starving';

/**
 * The ten slots along the bottom of the screen.
 *
 * ⚠️ **A literal here and `INVENTORY_ON_HAND` in `@lofi/shared`, and they are
 * the same number by agreement rather than by import.** This module may not
 * reach for the workspace — the four design generators in `tools/` resolve it
 * directly as a plain file — which is the same constraint that keeps the block
 * palette out of `hud-parts.ts`. `hud-scene.test.ts` is what checks the two
 * agree.
 */
const HAND_SLOTS = 10;

/**
 * 👕 **The four slots on your body, head downwards** (T153).
 *
 * ⚠️ **The same order as `CLOTHING_ORDER` in `@lofi/shared`, agreed rather than
 * imported**, for the reason above. Order is meaning: the first is slot 34 and
 * the last is slot 37, so this must never be reordered.
 */
const CLOTHING: readonly Garment[] = ['head', 'body', 'legs', 'feet'];

/**
 * 👕 What the four slots are called when the caller passes nothing.
 *
 * 🎯 **haugur, 2026-08-19**, asked to correct them: *"they are fine."* The game
 * hands these down from `en.json`; the sample sheets take these.
 */
const DEFAULT_GARMENT_NAMES: readonly string[] = ['Húfa', 'Peysa', 'Buxur', 'Skór'];

/**
 * What the open pack says, in the player's own language.
 *
 * 🔴 **Passed in, with the English as the default.** Standards §8b says every
 * sentence a player reads lives in `en.json` — but this module cannot import
 * the loader for the reason above. So the game hands the words down and the
 * sample sheets take the defaults, which is also what keeps samples 30–40
 * byte-identical across this change.
 */
export interface PackLabels {
  readonly title?: string | undefined;
  readonly close?: string | undefined;
  readonly onHand?: string | undefined;
  /** 👕 The heading over the four clothing slots. */
  readonly onYou?: string | undefined;
  /**
   * 🗑️ The word under the bin.
   *
   * ✅ **`Henda` — haugur confirmed it, 2026-08-19:** *"Henda is right."* It
   * means *to throw away*. ⚠️ It was proposed by an assistant and marked
   * unchecked until he looked, which is the rule for every Icelandic word this
   * project invents rather than takes from him.
   */
  readonly discard?: string | undefined;
  /**
   * 👕 The four garment names, under the slots, head downwards.
   *
   * 🎯 **haugur confirmed these on 2026-08-19** — *húfa*, *peysa*, *buxur*,
   * *skór*. Icelandic, like every other item name the game prints.
   */
  readonly garments?: readonly string[] | undefined;
  /** 🔨 The two page tabs — T130 slice 3. */
  readonly tabPack?: string | undefined;
  readonly tabCraft?: string | undefined;
}

export function makeScene({
  SW = 1280,
  SH = 800,
  SLOT = 48,
  GAP = 4,
  TOPBAR = 34,
}: SceneSize = {}) {
  const P = makeParts({ SLOT, GAP });
  const { C, SERIF, SANS, BARW, txt, rect, icFlame, icAskur, icRime, icHeat, hotbar } = P;
  const { carvedBar, carvedExposure, tallyBar, tallyExposure, haloed, struck, keyed } = P;

  const X0 = Math.round((SW - BARW) / 2); // 382 at 1280
  const HOTBAR_Y = SH - 14 - SLOT; // 738 at 800
  const BAR_H = 11;
  const EXPO_W = 300;
  const EXPO_X = X0 + Math.round((BARW - EXPO_W) / 2);

  /* The vertical rhythm the panel used to enforce, kept without the panel. */
  const EXPO_Y = HOTBAR_Y - 16 - BAR_H; // 711
  const FOOD_Y = EXPO_Y - 13 - BAR_H; // 687
  const HEALTH_Y = FOOD_Y - 7 - BAR_H; // 669
  const CONTROLS_Y = HEALTH_Y - 13; // 656 — baseline
  /**
   * 🐑 **The aim line — what the ANIMAL you are aiming at needs** — T286.
   *
   * 🎯 **haugur, 2026-10-02:** *"its hard aiming at a sheep and having to revert
   * your eyes sideways on the screen while still keeping the sheep in focus."*
   * It lived in the corner card, and the crosshair is in the middle.
   *
   * ⚠️ **Its OWN row, above the controls/reason row, never sharing it** — that
   * row already carries your own hunger reason, and *"A HUNGRY sheep"* replacing
   * *"you are starving"* would hide the more urgent of the two.
   */
  const AIM_Y = CONTROLS_Y - 20; // 636 — baseline, inside the scrim

  /** The bottom strip the scrim covers. Nothing above this is ever painted. */
  const SCRIM_H = 190;

  const VARIANTS: Record<
    VariantName,
    { rod: 'carved' | 'tally'; scrim: number; batten: boolean; numbers: boolean }
  > = {
    /* A — nothing but the object. The rod is the whole design. */
    bare: { rod: 'carved', scrim: 0, batten: false, numbers: false },
    /* B — each reading gets its OWN ground, sized to itself. Three small battens
       with the world showing between them, instead of one slab behind all of it.
       ⚠️ This is the only one that keeps the numbers, so the number question can
       be judged at the same time. That is deliberate, and it is a confound —
       read the rod, not the digits, when comparing B with A. */
    battens: { rod: 'carved', scrim: 0, batten: true, numbers: true },
    /* C — the stripped version. No rod at all: ten struck marks on a hairline.
       The least ink this can be made of, and therefore the honest test of how far
       "no background" goes before the reading stops arriving in the corner of
       your eye. */
    tally: { rod: 'tally', scrim: 0, batten: false, numbers: false },
    /* D — ✅ **the one haugur chose.** The rod, plus a scrim with no edges. Not a
       panel: a gradient from nothing to nótt at 62 % over the bottom 190 px, so
       there is no rectangle anywhere and nothing to be bulky, but the world
       quietens under the HUD.

       🔴 **And at night it does nothing at all, which is the point.** nótt is
       luminance 0.0069 and the night ground under the HUD measures 0.0000–0.0220
       with a mean of 0.0083 — so painting nótt over it changes almost nothing.
       The scrim is self-limiting: it can only ever subtract brightness that is
       there, so it buys margin at midday and cannot make a dark scene darker
       than nótt. See §13.14. */
    scrim: { rod: 'carved', scrim: 0.62, batten: false, numbers: false },
  };

  /**
   * The one reserved row above the bars, where the controls hint and the reason
   * both go — **the collision fix, and it is the design's rather than anyone's
   * invention.** The game's old `.controls` strip sat at `bottom: 12px`, exactly
   * where the on-hand row goes.
   *
   * 🔴 **One function so there is one code path.** The game fades this line and
   * the sheets do not, so the game draws it separately from the cluster; if it
   * drew it with its own copy of the call, the sheets and the screen would
   * differ in the one mark that is hardest to make legible.
   */
  const reasonLine = (text: string, o: StruckOptions = {}): string =>
    struck(SW / 2, CONTROLS_Y, text, { anchor: 'middle', size: 11, ...o });

  /**
   * ⌨️ **The controls hint, BELOW the on-hand row** — 2026-10-02, bug #830.
   *
   * 🔴 **It used to share the reason's row, and the two were drawn ON TOP OF
   * EACH OTHER.** The sheets drew one or the other in `cluster`, but the game
   * draws the hint in its own layer (so it can fade) and nothing there knew a
   * reason was showing — so *"you are cold"* sat in pale blue behind *"Click to
   * look around"* and could not be read. 🎯 haugur: *"you should move the key
   * and movement directions to the bottom below on hand bar."*
   *
   * 📏 The row leaves 14 px under the slots; an 11 px line on a baseline 3 px
   * off the bottom fits in it with its descenders.
   */
  const HINT_Y = SH - 3;
  const hintLine = (text: string, o: StruckOptions = {}): string =>
    struck(SW / 2, HINT_Y, text, { anchor: 'middle', size: 11, ...o });

  /** 🐑 T286 — the aim line, coloured by how urgent it is. */
  const aimLine = (text: string, tone: AimTone): string =>
    struck(SW / 2, AIM_Y, text, {
      anchor: 'middle',
      size: 14,
      weight: tone === 'plain' ? 500 : 700,
      fill: tone === 'starving' ? C.hverleir : tone === 'hungry' ? C.lampi : C.kerti,
    });

  /** A batten: a plate the size of ONE reading, not of the whole cluster. */
  function batten(x: number, y: number, w: number, h: number): string {
    const g: string[] = [];
    g.push(keyed(x, y, w, h));
    g.push(rect(x, y, w, h, C.nott, { op: 0.86 }));
    g.push(rect(x, y, w, 1, C.kerti, { op: 0.1 }));
    return g.join('');
  }

  function cluster(name: VariantName, state: ClusterState): string {
    const { health, food, expo, reason = null, contents, selected, controls = null } = state;
    // eslint-disable-next-line security/detect-object-injection -- `name` is a VariantName, a literal union.
    const V = VARIANTS[name];
    const g: string[] = [];
    const hurt = health < 30;
    const hcol = hurt ? C.hverleir : C.kerti;
    const bar = V.rod === 'tally' ? tallyBar : carvedBar;
    const gauge = V.rod === 'tally' ? tallyExposure : carvedExposure;

    if (V.scrim)
      g.push(
        `<rect x="0" y="${SH - SCRIM_H}" width="${SW}" height="${SCRIM_H}" fill="url(#scrim)"/>` +
          `<defs><linearGradient id="scrim" x1="0" y1="0" x2="0" y2="1">` +
          `<stop offset="0" stop-color="${C.nott}" stop-opacity="0"/>` +
          `<stop offset="1" stop-color="${C.nott}" stop-opacity="${V.scrim}"/>` +
          `</linearGradient></defs>`,
      );

    /* ⚠️ The battens hug as tightly as the icon allows — 3 px of margin, not 5.
       They are drawn tight on purpose, because the objection to this idea is that
       three plates at the cluster's own spacing simply become the panel again,
       and giving them loose margins would be arguing the case rather than testing
       it. **Look at how much world is left between them.** */
    if (V.batten) {
      const bw = BARW + (V.numbers ? 66 : 38);
      g.push(batten(X0 - 28, HEALTH_Y - 3, bw, BAR_H + 6));
      g.push(batten(X0 - 28, FOOD_Y - 3, bw, BAR_H + 6));
      g.push(batten(EXPO_X - 30, EXPO_Y - 6, EXPO_W + 60, BAR_H + 12));
    }

    /* ⚠️ The controls line is the hardest thing on this screen: 11 px of tólg with
       no ground at all. The panel used to hold it. Here it gets the stack-count
       treatment instead — a 3 px key-coloured stroke behind the glyphs — which
       §13.10 already proved holds over unknown ground. **If it fails, that is the
       finding, and it is in the report rather than fixed by putting the slab back.** */
    if (controls) g.push(hintLine(controls));

    g.push(haloed(icFlame, X0 - 22, HEALTH_Y - 1, 13, hcol));
    g.push(bar(X0, HEALTH_Y, BARW, health, hcol, BAR_H));

    g.push(haloed(icAskur, X0 - 22, FOOD_Y - 1, 13, C.lampi));
    g.push(bar(X0, FOOD_Y, BARW, food, C.lampi, BAR_H));

    const ecol = expo < 0 ? C.kalt : expo > 0 ? C.rodi : C.tolg;
    if (expo !== 0) g.push(haloed(expo < 0 ? icRime : icHeat, EXPO_X - 24, EXPO_Y - 2, 13, ecol));
    g.push(gauge(EXPO_X, EXPO_Y, EXPO_W, expo, BAR_H));

    /* 🔴 The numbers are GONE in three of the four. haugur asked for splendour and
       a two-digit number is the least splendid thing that can be on a screen —
       and with nine countable notches it is telling you what the rod already
       said. B keeps them so the two can be compared rather than argued about. */
    if (V.numbers) {
      g.push(struck(X0 + BARW + 26, HEALTH_Y + 9, String(health), { fill: hcol, weight: 700 }));
      g.push(struck(X0 + BARW + 26, FOOD_Y + 9, String(food), { fill: C.lampi, weight: 700 }));
    }

    /* D64 §7 — the HUD says WHY, in words, and only when there is a why.
       ⚠️ It takes the controls line's row rather than sitting between the bars.
       Placed just above the exposure gauge it landed **on top of the food rod**,
       which is what happens when a thing is positioned relative to the element it
       explains rather than to the space that is free. A reason outranks a
       controls hint, so they share one line and the reason wins. */
    if (reason) g.push(reasonLine(reason, { size: 12, fill: ecol, weight: 500 }));

    g.push(hotbar(V.rod === 'tally' ? 'tally' : 'carved', X0, HOTBAR_Y, contents, selected));
    return g.join('');
  }

  /**
   * 🔴 **Where every part of the open pack sits — ONE set of numbers.**
   *
   * The drawing needs them to put slots on the screen and the mouse needs them
   * to work out which slot was clicked. **Two copies would be two things to keep
   * in step**, and the first symptom of them drifting is a click that lands one
   * slot to the left — which looks like a rendering fault and is not one. This
   * is the same argument that moved the whole module into the client (T149).
   *
   * ⚠️ **8 × 3, not 6 × 4** — `Design/00_Visual_Language.md` §13.6: ten and
   * twenty-four do not share a column count, so the pack is drawn *narrower*
   * than the hand row and separated by a rule, and therefore reads as a
   * different place rather than as a fourth row of one grid.
   */
  /**
   * 🔴 **Where every one of these panels ENDS**, and the only number of the
   * three that is load-bearing.
   *
   * T170 measured **2 px** between the bottom of the pack and the health rod at
   * 1.75×. The pack and the hearth reach it by starting at 190 and being 480
   * tall; ⚠️ **the chest cannot** — it draws 27 more slots than either — so it
   * starts higher and measures up from here instead. **Named once so the three
   * cannot drift apart**, and `hud-scene.test.ts` pins them together.
   */
  const PACK_BOTTOM = 190 + 480;

  function packLayout() {
    const cols = 8;
    const rows = 3;
    const PAD = 20;
    const w = BARW + PAD * 2;
    const x = Math.round((SW - w) / 2);
    const y = 190;
    const titleH = 46;
    const packW = cols * SLOT + (cols - 1) * GAP;
    const packH = rows * SLOT + (rows - 1) * GAP;
    /* The pack grid, centred inside the panel above the rule. */
    const px = x + PAD + Math.round((BARW - packW) / 2);
    const py = y + titleH;
    /* The rule, and the hand row under it. */
    const ry = py + packH + 16;
    const hx = x + PAD;
    const hy = ry + 24;
    /* 👕 And below the hand row, the four you are wearing — in the space
       `Design/00_Visual_Language.md` §13.6 reserved for exactly this. The panel
       grows downward to hold them and nothing above moves, which is why this
       option was chosen over a column beside the pack: our panel has no spare
       margin on the left, so a column would have widened it and pushed the
       title off centre. */
    const cry = hy + SLOT + 16;
    const cx = hx;
    const cy = cry + 24;
    /* Room under the slots for the garment names, then the padding. */
    const h = cy + SLOT + 26 + SLOT + 12 + PAD - y;
    return {
      cols,
      rows,
      PAD,
      x,
      y,
      w,
      h,
      titleH,
      packW,
      packH,
      px,
      py,
      ry,
      hx,
      hy,
      cry,
      cx,
      cy,
      /* 🗑️ The bin — far right, and on its OWN row below the garments (T154).
         🔴 **Its distance from every slot is the safety, and the first attempt
         got it wrong.** Put level with the clothing row it sat **40 px directly
         below the last slot on hand** — under a target you drop into constantly,
         which is the worst place on the panel rather than the safest. The test
         that caught it exists because the claim "it is far from everything" is
         the whole protection and was otherwise only a comment.
         ⚠️ **The hand row spans the full width**, so no position on the line
         under it is clear of every slot. Vertical distance is the only escape. */
      bx: x + PAD + BARW - SLOT,
      by: cy + SLOT + 26,
      SLOT,
      GAP,
    };
  }

  /**
   * 🖱️ **Which slot is under this point?** `null` for anywhere else on the panel.
   *
   * Answers in **inventory slot numbers, 0–33** — the numbering the protocol and
   * `@lofi/shared/inventory.ts` both use — so nothing downstream has to know
   * that the pack is drawn as a grid and the hand as a row. Slots 0–9 are the
   * hand row along the bottom of the panel; 10–33 are the grid above it.
   *
   * ⚠️ **The gaps between slots are not part of any slot.** A click that lands
   * in one moves nothing, which is the honest answer — snapping to the nearest
   * slot would move somebody's things somewhere they did not point at.
   */
  /**
   * 🔨 **The two tabs at the top of the pack** — T130 slice 3, **D95**.
   *
   * 🎯 **haugur chose a page toggle** over widening the panel: *"1, lets see
   * it."* The panel is 556 px wide and already runs the height of the screen,
   * and `packLayout`'s own comment records that a side column was rejected once
   * before for exactly that reason.
   *
   * 🔴 **English defaults, like every other label in this file.** 🎯 haugur,
   * 2026-08-27: *"this should all be in english until we do translations."* The
   * game hands down whatever `en.json` says. ⚠️ **Item NAMES are a different
   * thing and stay Icelandic** — they come from `items.json`, and the pack has
   * drawn them that way since it existed.
   *
   * ⚠️ **The widths here are the same arithmetic `tabAt` hit-tests with**, so a
   * click cannot land on a tab the eye says it missed — the rule `packSlotAt`
   * already follows.
   */
  const TAB_SIZE = 20;
  const TAB_GAP = 18;
  /** One tab's width, from its label. Drawing and hit-testing share it. */
  const tabW = (label: string): number => label.length * 11;

  function tabLabels(labels: {
    title?: string | undefined;
    tabPack?: string | undefined;
    tabCraft?: string | undefined;
  }): [string, string] {
    /* ⚠️ The Pack tab takes `title` — the pack already had a name and a
       second key for the same word is a second thing to keep true. */
    return [labels.tabPack ?? labels.title ?? 'Pack', labels.tabCraft ?? 'Craft'];
  }

  function tabRow(
    active: 'pack' | 'craft',
    labels: {
      title?: string | undefined;
      tabPack?: string | undefined;
      tabCraft?: string | undefined;
    },
  ): string {
    const L = packLayout();
    const [packLabel, craftLabel] = tabLabels(labels);
    const baseY = L.y + 30;
    const packX = L.x + L.PAD;
    const craftX = packX + tabW(packLabel) + TAB_GAP;
    const g: string[] = [];
    g.push(
      txt(packX, baseY, packLabel, {
        size: TAB_SIZE,
        font: SERIF,
        weight: 500,
        fill: active === 'pack' ? C.kerti : C.tolg,
      }),
    );
    g.push(
      txt(craftX, baseY, craftLabel, {
        size: TAB_SIZE,
        font: SERIF,
        weight: 500,
        fill: active === 'craft' ? C.kerti : C.tolg,
      }),
    );
    /* The lit tab is underlined rather than boxed — there are no rectangles
       anywhere in this HUD, §13. */
    const ux = active === 'pack' ? packX : craftX;
    const uw = tabW(active === 'pack' ? packLabel : craftLabel);
    g.push(rect(ux, baseY + 8, uw, 2, C.lampi, { op: 0.85 }));
    return g.join('');
  }

  /** Which tab the mouse is over, or `null`. */
  function tabAt(
    mx: number,
    my: number,
    labels: {
      title?: string | undefined;
      tabPack?: string | undefined;
      tabCraft?: string | undefined;
    } = {},
  ): 'pack' | 'craft' | null {
    const L = packLayout();
    const [packLabel, craftLabel] = tabLabels(labels);
    const baseY = L.y + 30;
    if (my < baseY - TAB_SIZE || my > baseY + 10) return null;
    const packX = L.x + L.PAD;
    const craftX = packX + tabW(packLabel) + TAB_GAP;
    if (mx >= packX && mx <= packX + tabW(packLabel)) return 'pack';
    if (mx >= craftX && mx <= craftX + tabW(craftLabel)) return 'craft';
    return null;
  }

  /** Geometry of the crafting list, shared by the drawing and the hit test. */
  const CRAFT_ROW_H = 66;

  /**
   * 🗂️ **The row of category tabs, and the line the recipes start on** — D122,
   * T249.
   *
   * 🎯 **haugur, 2026-09-22:** *"i think we should do a multitab menu for
   * crafting, so fr instance having a tab for tools, a tab for house materials
   * etc."* 🔴 **Because five rows fit and the bench had thirteen recipes**, so
   * eight were invisible — including the spjót he had gone there to make.
   *
   * ⚠️ **`craftTop` moved down 24 px to make room and the list still holds FIVE
   * rows** — 358 px of room against a 66 px row. **Checked rather than hoped**:
   * `hud-scene.test.ts` asserts no tab is longer than what fits, so if this ever
   * costs a row the build says so instead of the screen hiding one.
   */
  const CRAFT_TAB_SIZE = 13;
  const CRAFT_TAB_GAP = 16;
  const craftTabY = (): number => packLayout().y + 84;
  const craftTabW = (label: string): number => label.length * 7 + 4;
  const craftTop = (): number => packLayout().y + 102;

  /**
   * 🗂️ Where each category tab begins, in draw order.
   *
   * 🔴 **One function, asked by the drawing AND by the hit test.** Two copies of
   * a layout drifting apart is the fault this repo names oftener than any other,
   * and on this very screen it once made *"clicking empty blackness make a stone
   * pick."*
   */
  function craftTabXs(labels: readonly string[]): number[] {
    const L = packLayout();
    let at = L.x + L.PAD;
    return labels.map((label) => {
      const here = at;
      at += craftTabW(label) + CRAFT_TAB_GAP;
      return here;
    });
  }

  /**
   * 🗂️ Which category tab the mouse is over, as an INDEX into the list given,
   * or `null`.
   *
   * ⚠️ **An index, not a name** — this module never learns what a category is
   * called or what it means. The caller holds the list and the words, exactly as
   * it does for the recipe rows.
   */
  function craftTabAt(mx: number, my: number, labels: readonly string[]): number | null {
    if (labels.length === 0) return null;
    const baseY = craftTabY();
    if (my < baseY - CRAFT_TAB_SIZE || my > baseY + 8) return null;
    const xs = craftTabXs(labels);
    for (const [i, x0] of xs.entries()) {
      const label = labels.at(i) ?? '';
      if (mx >= x0 && mx <= x0 + craftTabW(label)) return i;
    }
    return null;
  }

  /** 🗂️ The category tabs, drawn. `active` is an index into `labels`. */
  function craftTabRow(labels: readonly string[], active: number): string {
    if (labels.length === 0) return '';
    const baseY = craftTabY();
    const xs = craftTabXs(labels);
    const g: string[] = [];
    for (const [i, x0] of xs.entries()) {
      const label = labels.at(i) ?? '';
      g.push(
        txt(x0, baseY, label, {
          size: CRAFT_TAB_SIZE,
          font: SERIF,
          fill: i === active ? C.kerti : C.tolg,
        }),
      );
      /* The lit tab is underlined rather than boxed — there are no rectangles
         anywhere in this HUD, §13, and the Pack/Craft row above does the same. */
      if (i === active) {
        g.push(rect(x0, baseY + 6, craftTabW(label), 2, C.lampi, { op: 0.85 }));
      }
    }
    return g.join('');
  }

  /**
   * 🔴 **How many recipe rows the panel can actually hold.**
   *
   * ⚠️ **This number is the whole bug of 2026-09-02, and it is why it is a
   * function rather than a literal in two places.** `craftPage` used to draw
   * rows until one fell past the bottom and then `break`, with nothing on the
   * screen to say it had stopped. The bench list reached **nine** the day the
   * chest was built and the panel holds **five**, so 🎯 haugur went looking for
   * the chest recipe and it was simply not there — and neither, for a day
   * before that, were **steinöxi, steinhaki and steinskófla**.
   *
   * 🔴 **A silent truncation is the worst shape a list bug can take**: nothing
   * is drawn wrong, so the screen looks finished, and the only evidence is a
   * recipe a player cannot find. ✅ The fix is that the list SCROLLS and says
   * when there is more — and that the drawing and the hit test both ask this
   * one function how many rows there are room for.
   *
   * ⚠️ **The list does not fit and cannot be made to fit.** Nine rows at a
   * readable height want ~692 px between the top bar and the hotbar and there
   * are 636; the búr and the double kista will make it longer still. Scrolling
   * is the answer that keeps working, rather than a taller panel that buys one
   * more recipe.
   */
  function craftRowsFit(): number {
    const L = packLayout();
    const room = L.y + L.h - L.PAD - craftTop();
    return Math.max(0, Math.floor(room / CRAFT_ROW_H));
  }

  /**
   * 🔴 **The furthest down the list you may scroll**, so the last row sits at
   * the bottom of the panel rather than the list running off into blank space.
   *
   * ✅ **Clamping lives here, next to `craftRowsFit`**, so the panel, the hit
   * test and the wheel cannot each have their own idea of how far is too far.
   */
  function craftMaxScroll(rows: number): number {
    return Math.max(0, rows - craftRowsFit());
  }

  /**
   * 🎡 **The line under the list that says there is more — and takes a click.**
   *
   * 🎯 **haugur, 2026-10-01:** *"make it scrollable but visiably so user see
   * well that thei can scroll."* 🔴 **The counter and a 4 px rail had shipped
   * on 2026-09-02 and were not enough** — a tab that fills up was then banned by
   * a test rather than trusted to scroll. ✅ **So the list now SAYS it goes on**,
   * in words, in the lamp colour, under the last row: *"▼ 2 more below"*, and
   * *"▲ 2 above"* once you are at the bottom.
   *
   * ⚠️ **It sits on the last row's own bottom margin**, the 10 px under its
   * hairline plus the panel's padding — so it costs no row at any window size.
   */
  const craftCueY = (): number => craftTop() + craftRowsFit() * CRAFT_ROW_H + 2;

  /**
   * 🎡 **Which way a click on the cue scrolls** — `+1` a page down, `-1` a page
   * up, or `null` when the click is not on it or the list fits.
   */
  function craftCueAt(mx: number, my: number, rows: number, scroll = 0): 1 | -1 | null {
    const L = packLayout();
    if (rows <= craftRowsFit()) return null;
    if (mx < L.x + L.PAD || mx > L.x + L.w - L.PAD) return null;
    const y = craftCueY();
    /* 🔴 From the last row's hairline down — never into the row itself, or a
       click meant for the bottom recipe would scroll instead of crafting. */
    if (my < y - 12 || my > y + 8) return null;
    const first = Math.min(Math.max(0, Math.trunc(scroll)), craftMaxScroll(rows));
    if (first + craftRowsFit() < rows) return 1;
    return first > 0 ? -1 : null;
  }

  /**
   * Which recipe row the mouse is over, or `null`.
   *
   * 🔴 **`scroll` is the index of the first row DRAWN, and this returns the
   * index into the whole list** — the caller asked which recipe, not which line
   * of the screen.
   *
   * ⚠️ **It refuses a point below the last drawn row, and that is a fix rather
   * than tidiness.** The old version bounded only against the row COUNT, so on
   * a list longer than the panel a click in the dark below the panel answered
   * with a recipe that was never drawn — clicking empty blackness made a stone
   * pick.
   */
  function craftRowAt(mx: number, my: number, rows: number, scroll = 0): number | null {
    const L = packLayout();
    if (mx < L.x + L.PAD || mx > L.x + L.w - L.PAD) return null;
    const line = Math.floor((my - craftTop()) / CRAFT_ROW_H);
    if (line < 0 || line >= craftRowsFit()) return null;
    const i = line + scroll;
    return i >= 0 && i < rows ? i : null;
  }

  /**
   * 🔨 **One row of the crafting list.**
   *
   * ⚠️ **Everything here is passed in.** This file must not know what a recipe
   * is — `recipes.ts` answers *can I make it* and *what am I short of*, and this
   * only draws the answer. The same separation `slotContentFor` keeps.
   */
  interface CraftRow {
    /** What you get, drawn as a slot. */
    readonly result: SlotContent;
    /** The Icelandic name of the result. */
    readonly name: string;
    /** 🖼️ The picture — cells left to right, top to bottom. `null` is a hole. */
    readonly picture: readonly (SlotContent | null)[];
    /** How wide the picture is. 🔴 Not guessable from the cells — see `recipes.ts`. */
    readonly cols: number;
    /** ✅ Can you make it right now, here? */
    readonly can: boolean;
    /**
     * ⚠️ **What you are short of, already worded.** Empty when you can make it.
     *
     * 🔴 **The whole reason this row exists rather than a grey box** — D95:
     * *"I clicked it and nothing happened"* is a bug report.
     */
    readonly short: string;
  }

  /**
   * 🔨 **The crafting page — D95.**
   *
   * 🎯 **haugur, 2026-08-27:** one list, inside the pack, on `E`; recipes you
   * cannot make are **shown and greyed with what you lack**; recipes you have
   * not learned are **not here at all**.
   *
   * ⚠️ **A PAGE, not a column.** The panel is 556 px wide and already runs from
   * y=190 to y=686 on an 800 px screen. `packLayout`'s own comment records that
   * a side column was rejected once before, for the clothing slots, because
   * widening pushes the title off centre. 🎯 **haugur chose the page toggle**
   * over widening the panel.
   */
  function craftPage(
    name: VariantName,
    rows: readonly CraftRow[],
    /** The row the mouse is over, or `null`. */
    hover: number | null = null,
    labels: {
      title?: string | undefined;
      close?: string | undefined;
      tabPack?: string | undefined;
      tabCraft?: string | undefined;
      /** What the page says when there is nothing on it. */
      empty?: string | undefined;
      /** 🎡 The cue under a list with rows hidden below — `{count}` already filled in. */
      more?: string | undefined;
      /** 🎡 The cue once scrolled to the bottom — how many rows are above. */
      above?: string | undefined;
    } = {},
    /** What the header says you are standing at — `null` for your own hands. */
    station: string | null = null,
    /**
     * 🎡 **The first row drawn.** Clamped here as well as by the caller — a
     * screen drawn past the end of its own list is the fault this is fixing.
     */
    scroll = 0,
    /**
     * 🗂️ **The category tabs, as finished words** — D122, T249.
     *
     * ⚠️ **This module never learns what a category IS.** It is handed the
     * words and which one is lit, exactly as it is handed the recipe rows —
     * the same rule that keeps every sentence on this screen in `en.json`.
     * **An empty list draws no tabs at all**, which is right for a station
     * with only one kind of thing on it.
     */
    tabs: readonly string[] = [],
    /** Which of {@link tabs} is lit, as an index. */
    activeTab = 0,
  ): string {
    // eslint-disable-next-line security/detect-object-injection -- `name` is a VariantName, a literal union.
    const k = VARIANTS[name].rod === 'tally' ? 'tally' : 'carved';
    const g: string[] = [];
    const L = packLayout();
    const { PAD, x, y, w, h } = L;

    g.push(rect(0, TOPBAR, SW, SH - TOPBAR, C.nott, { op: 0.58 }));
    g.push(keyed(x, y, w, h));
    g.push(rect(x, y, w, h, C.nott, { op: 0.95 }));
    g.push(rect(x, y, w, 1, C.kerti, { op: 0.14 }));

    /* ── The two tabs. The title is a tab now, and the inactive one is tolg so
       there is exactly one bright thing in the row. ─────────────────────── */
    g.push(tabRow('craft', labels));
    g.push(
      txt(x + w - PAD, y + 29, labels.close ?? 'E  or  Esc  to close', {
        size: 11,
        fill: C.tolg,
        anchor: 'end',
      }),
    );

    /* Where you are standing, because a bench shows more than your hands do.
       🗂️ On the RIGHT since D122 — the left of this line is the category tabs,
       and the station is the quieter of the two things. */
    g.push(
      txt(x + w - PAD, y + 62, station ?? 'In your hands', {
        size: 11,
        fill: C.tolg,
        anchor: 'end',
      }),
    );

    /* 🗂️ The category tabs — D122, T249. Nothing here knows what a category
       means; the caller hands down the words and which one is lit. */
    g.push(craftTabRow(tabs, activeTab));

    /* ── The rows ─────────────────────────────────────────────────────────── */
    const ROW_H = CRAFT_ROW_H;
    const CELL = 18;
    const top = craftTop();

    /* 🎡 The window of the list that is on screen. `first` is clamped so a
       stale scroll — the list got shorter while it was open — cannot draw past
       the end, and `hover` is an index into the WHOLE list, so the comparison
       below adds `first` back rather than subtracting it from the hover. */
    const fit = craftRowsFit();
    const first = Math.min(Math.max(0, Math.trunc(scroll)), craftMaxScroll(rows.length));
    const shown = Math.min(fit, rows.length - first);

    for (let line = 0; line < shown; line++) {
      const i = first + line;
      // eslint-disable-next-line security/detect-object-injection -- `i` is bounded by `rows.length` through `first` and `shown`.
      const r = rows[i]!;
      const ry = top + line * ROW_H;

      if (hover === i) g.push(rect(x + PAD - 6, ry - 6, w - PAD * 2 + 12, ROW_H - 6, C.badstofa));
      g.push(rect(x + PAD, ry + ROW_H - 10, w - PAD * 2, 1, C.hairfaint));

      /* What you get. ⚠️ A greyed row still draws its picture — you are meant
         to be able to read what it needs, which is the point of showing it. */
      g.push(P.slot(k, x + PAD, ry, r.can ? r.result : { ...r.result, count: undefined }));

      const tx = x + PAD + SLOT + 14;
      g.push(txt(tx, ry + 18, r.name, { size: 14, font: SERIF, fill: r.can ? C.kerti : C.tolg }));
      if (!r.can && r.short) {
        g.push(txt(tx, ry + 36, r.short, { size: 11, fill: C.rodi }));
      }

      /* 🖼️ The picture, right-aligned. It teaches the shape and is never
         matched against — D88. */
      const pw = r.cols * (CELL + 3) - 3;
      const px0 = x + w - PAD - pw;
      for (let c = 0; c < r.picture.length; c++) {
        // eslint-disable-next-line security/detect-object-injection -- `c` is this loop’s own counter, bounded by `r.picture.length`.
        const cell = r.picture[c];
        const cx = px0 + (c % r.cols) * (CELL + 3);
        const cy = ry + 4 + Math.floor(c / r.cols) * (CELL + 3);
        /* ⚠️ `null` is a HOLE in the picture — the space in the middle of the
           hearth's ring of stones. `undefined` is a short array, which is a
           different thing and draws nothing either. */
        if (cell === null || cell === undefined) continue;
        /* 🎨 **The real block face where there is one** — 🎯 haugur, 2026-08-27:
           *"use the real textures."* ⚠️ Falls back to the block's flat colour,
           which is what every slot in the pack already does for an item with no
           drawn icon. */
        if (cell.iconUri !== undefined) {
          g.push(
            `<image x="${String(cx)}" y="${String(cy)}" width="${String(CELL)}" height="${String(CELL)}" href="${cell.iconUri}"${r.can ? '' : ' opacity="0.45"'}/>`,
          );
        } else {
          g.push(rect(cx, cy, CELL, CELL, cell.colour ?? C.hola, { op: r.can ? 1 : 0.45 }));
        }
      }
    }

    /* 🔴 **Which part of the list you are looking at — and it is the whole
       point of this change.** A truncated list that says nothing is how the
       chest went missing: the screen looked complete, so nobody scrolled and
       nobody suspected a list. ⚠️ **Digits and a slash rather than a word**, so
       it needs no translating and reads the same to everybody — the two tabs
       and the empty line are the only text on this page that does.
       ✅ Drawn only when there is something hidden; a list that fits says
       nothing, because there is nothing to say. */
    if (rows.length > fit) {
      /* ⚠️ On the TAB line since D122, because the station moved into the
         place this used to hold. 🔴 It should now be unreachable — every tab
         is shorter than the rows that fit, and a test says so — but it is kept
         rather than deleted: **the day a tab overflows, this is the only thing
         on screen that would admit it.** */
      g.push(
        txt(
          x + w - PAD,
          craftTabY(),
          `${String(first + 1)}–${String(first + shown)} / ${String(rows.length)}`,
          { size: 11, fill: C.kerti, anchor: 'end' },
        ),
      );

      /* 🎡 **And a rail, because the counter was not enough.** 🎯 **haugur,
         2026-09-02, after the counter had shipped:** *"there is no visual
         indicator that you can scroll down the list if there is more on it than
         fits the screen."*

         🔴 **A number tells you the list is long; a rail tells you it MOVES.**
         The two say different things and the second is the one that invites a
         hand to the wheel — which is why this is drawn as well as, rather than
         instead of, the digits.

         ⚠️ **It sits in the PANEL'S PADDING**, outside the rows, because the
         recipe picture is already right-aligned against the inner edge and a
         rail over it would sit on top of the teaching. **The gutter is 20 px
         wide and this takes 3 of them.**

         ⚠️ **The thumb has a floor of 20 px.** Proportional height alone would
         shrink to a few pixels on a long list and read as a speck of dust
         rather than as a handle. */
      /* 🎡 2026-10-01: 4 px → 7 px and the thumb in the LAMP colour — the 4 px
         rail at 70% read as a hairline, which is why haugur asked again. */
      const RAIL_W = 7;
      const railX = x + w - PAD + 5;
      const railY = top;
      const railH = fit * ROW_H - 12;
      g.push(rect(railX, railY, RAIL_W, railH, C.kerti, { op: 0.2, rx: 3 }));
      const thumbH = Math.max(28, Math.round((railH * fit) / rows.length));
      const travel = railH - thumbH;
      const thumbY = railY + Math.round((travel * first) / craftMaxScroll(rows.length));
      g.push(rect(railX, thumbY, RAIL_W, thumbH, C.lampi, { op: 0.95, rx: 3 }));

      /* 🎡 And the cue in WORDS — see `craftCueY`. */
      const below = rows.length - first - shown;
      const cue =
        below > 0
          ? (labels.more ?? '▼   {count} more below').replace('{count}', String(below))
          : first > 0
            ? (labels.above ?? '▲   {count} above').replace('{count}', String(first))
            : '';
      if (cue !== '') {
        g.push(
          txt(x + w / 2, craftCueY(), cue, {
            size: 14,
            font: SERIF,
            fill: C.lampi,
            anchor: 'middle',
          }),
        );
      }
    }

    if (rows.length === 0) {
      g.push(
        txt(x + w / 2, y + h / 2, labels.empty ?? 'Nothing you can make here yet.', {
          size: 13,
          fill: C.tolg,
          anchor: 'middle',
        }),
      );
    }
    return g.join('');
  }

  function packSlotAt(mx: number, my: number): number | null {
    const L = packLayout();

    const inGrid = (gx: number, gy: number, cols: number, rows: number): number | null => {
      const col = Math.floor((mx - gx) / (SLOT + GAP));
      const row = Math.floor((my - gy) / (SLOT + GAP));
      if (col < 0 || col >= cols || row < 0 || row >= rows) return null;
      /* Inside the slot itself, rather than in the gap after it. */
      if (mx - gx - col * (SLOT + GAP) > SLOT) return null;
      if (my - gy - row * (SLOT + GAP) > SLOT) return null;
      return row * cols + col;
    };

    const inPack = inGrid(L.px, L.py, L.cols, L.rows);
    if (inPack !== null) return HAND_SLOTS + inPack;

    const inHand = inGrid(L.hx, L.hy, HAND_SLOTS, 1);
    if (inHand !== null) return inHand;

    /* 👕 And the four on your body, which are numbered after the pack. */
    const inWorn = inGrid(L.cx, L.cy, CLOTHING.length, 1);
    return inWorn === null ? null : HAND_SLOTS + L.cols * L.rows + inWorn;
  }

  /**
   * Where one slot number is drawn, in panel coordinates.
   *
   * The inverse of {@link packSlotAt}, and it exists so the hover outline and
   * the item riding the cursor are placed by the same arithmetic that decided
   * which slot was clicked.
   */
  /**
   * 🗑️ Is this point on the bin? (T154)
   *
   * ⚠️ **Separate from {@link packSlotAt} on purpose.** The bin is not a slot:
   * nothing lives in it, nothing can be taken out of it, and folding it into the
   * slot numbering would put "nowhere" inside the range every other rule
   * iterates over.
   */
  function packBinAt(mx: number, my: number): boolean {
    const L = packLayout();
    return mx >= L.bx && mx <= L.bx + SLOT && my >= L.by && my <= L.by + SLOT;
  }

  function packSlotRect(slot: number): { x: number; y: number } | null {
    const L = packLayout();
    const carried = HAND_SLOTS + L.cols * L.rows;
    if (!Number.isInteger(slot) || slot < 0 || slot >= carried + CLOTHING.length) return null;
    if (slot < HAND_SLOTS) return { x: L.hx + slot * (SLOT + GAP), y: L.hy };
    if (slot >= carried) return { x: L.cx + (slot - carried) * (SLOT + GAP), y: L.cy };
    const i = slot - HAND_SLOTS;
    return {
      x: L.px + (i % L.cols) * (SLOT + GAP),
      y: L.py + Math.floor(i / L.cols) * (SLOT + GAP),
    };
  }

  /** The pack, open. Same language: carved slots, doubled edges, no ornament. */
  function packPanel(
    name: VariantName,
    pack: readonly SlotContent[],
    hand: readonly SlotContent[],
    selected: number,
    /**
     * 🖱️ The slot the mouse is over, 0–33, or `null` for none.
     *
     * ⚠️ **Absent draws nothing at all**, which is what keeps the approved
     * sample sheets byte-for-byte identical across this change — the same rule
     * the item labels followed on 2026-08-18.
     */
    hover: number | null = null,
    labels: PackLabels = {},
    /** 👕 The four slots on your body, head downwards. Empty draws a ghost. */
    worn: readonly SlotContent[] = [],
    /** 🗑️ Whether something is being carried, so the bin can do anything. */
    armed = false,
  ): string {
    // eslint-disable-next-line security/detect-object-injection -- `name` is a VariantName, a literal union.
    const k = VARIANTS[name].rod === 'tally' ? 'tally' : 'carved';
    const g: string[] = [];
    const L = packLayout();
    const { cols, rows, PAD, x, y, w, h } = L;

    /* ⚠️ The pack KEEPS a panel, and that is not an inconsistency. The HUD is a
       thing you read while the world is moving; the pack is a thing you stop and
       look at, and the dimmed world behind it is what tells you the game is
       waiting for you. haugur's "bulky" was about the strip you stare past all
       day, not about a screen you opened on purpose. */
    /* ⚠️ From the top of this drawing, less whatever the status bar covers of
       it — see `TOPBAR`. A band of undimmed world is not a small blemish: the
       dimming is the whole of what says the game is waiting for you. */
    g.push(rect(0, TOPBAR, SW, SH - TOPBAR, C.nott, { op: 0.58 }));
    g.push(keyed(x, y, w, h));
    g.push(rect(x, y, w, h, C.nott, { op: 0.95 }));
    g.push(rect(x, y, w, 1, C.kerti, { op: 0.14 }));
    /* 🔨 The title is a TAB now — T130 slice 3, D95. haugur chose a page
       toggle, so the pack is one of two pages rather than the only one. */
    g.push(tabRow('pack', labels));
    g.push(
      txt(x + w - PAD, y + 29, labels.close ?? 'E  or  Esc  to close', {
        size: 11,
        fill: C.tolg,
        anchor: 'end',
      }),
    );

    for (let r = 0; r < rows; r++)
      for (let c = 0; c < cols; c++)
        g.push(
          P.slot(k, L.px + c * (SLOT + GAP), L.py + r * (SLOT + GAP), pack[r * cols + c] ?? {}),
        );

    g.push(rect(x + PAD, L.ry, BARW, 1, 'rgba(242,233,218,0.14)'));
    g.push(txt(x + PAD, L.ry + 16, labels.onHand ?? 'On hand — 10', { size: 11, fill: C.tolg }));
    g.push(hotbar(k, L.hx, L.hy, hand, selected));

    /* 👕 What you are wearing (T153). Its own rule and heading, because it is a
       third PLACE and not a fourth row of the pack — the same argument §13.6
       makes for keeping the hand row wider than the grid. */
    g.push(rect(x + PAD, L.cry, BARW, 1, 'rgba(242,233,218,0.14)'));
    g.push(txt(x + PAD, L.cry + 16, labels.onYou ?? 'On you — 4', { size: 11, fill: C.tolg }));
    CLOTHING.forEach((garment, i) => {
      const sx = L.cx + i * (SLOT + GAP);
      // eslint-disable-next-line security/detect-object-injection -- `i` is the index `forEach` is walking, over a four-element literal.
      const content = worn[i] ?? {};
      g.push(P.slot(k, sx, L.cy, content));
      /* 🔴 The ghost only while the slot is EMPTY. A mark under a real garment
         would read as a second thing in the slot. */
      if (content.item === undefined) g.push(P.ghost(garment, sx, L.cy));
      // eslint-disable-next-line security/detect-object-injection -- same counter, same four elements.
      const name = labels.garments?.[i] ?? DEFAULT_GARMENT_NAMES[i];
      if (name !== undefined)
        g.push(
          txt(sx + SLOT / 2, L.cy + SLOT + 12, name, { size: 9, fill: C.tolg, anchor: 'middle' }),
        );
    });

    /* 🗑️ The bin (T154). ⚠️ It only LOOKS armed while something is being
       carried, because that is the only moment it can do anything — a mark that
       looks live when it is inert is the same lie as a bar that looks full when
       nothing drives it. */
    g.push(P.discardMark(L.bx, L.by, armed));
    g.push(
      txt(L.bx + SLOT / 2, L.by + SLOT + 12, labels.discard ?? 'Henda', {
        size: 9,
        fill: C.tolg,
        anchor: 'middle',
      }),
    );

    /* 🖱️ The slot under the mouse, outlined last so it sits over everything.
       ⚠️ An outline and not a fill: a slot that lights up reads as *selected*,
       and exactly one slot in this panel is selected already — the one in hand.
       Two meanings sharing one appearance is how a screen starts lying. */
    const over = hover === null ? null : packSlotRect(hover);
    if (over)
      g.push(
        rect(over.x - 2, over.y - 2, SLOT + 4, SLOT + 4, 'none', {
          stroke: C.kerti,
          sw: 1.5,
          op: 0.55,
          rx: 2,
        }),
      );

    return g.join('');
  }

  /* ══ 🔥 The hearth, open — T128 ═══════════════════════════════════════════
     🎯 **haugur, 2026-08-27:** *"heart menu should show 2 slots for wood for
     burnig that can hold 99 each, then we need 4 slots for charcoal to be saved
     to, i would also want to be able to move wood and chacoal from my onhand
     inventory with the mouse, that adds the role of charcoal storage to the
     heath."*

     🔴 **Its own screen, NOT a page of the pack**, and haugur is why. Shown a
     proposal to make it a third tab beside `Pack` and `Craft`: *"if we put all
     the menus as tabs on the same menu, there is no need for players to walk to
     hearth or smíðabekkur."* ✅ `Development/Minecraft/04` §4 agrees — stations
     **site** progression, *"go home, or build a second one"*, and it marks that
     job **load-bearing**. ⚠️ A store you can reach from anywhere has stopped
     being a place. */

  /**
   * 🔥 The hearth's own slots — **two for wood, four for charcoal** (D96).
   *
   * ⚠️ **Literals here and `HEARTH_WOOD_SLOTS` / `HEARTH_CHARCOAL_SLOTS` in
   * `@lofi/shared`, agreed rather than imported** — the same constraint that
   * keeps `HAND_SLOTS` a literal: the design generators resolve this module as a
   * plain file and may not reach for the workspace. `hud-scene.test.ts` is what
   * checks the two agree.
   *
   * 🔴 **Order is meaning.** Wood is 0–1, the output shelf is 2–5 and **the two
   * cooking slots are 6–7**, and the protocol names slots by these numbers, so
   * this must never be reordered.
   *
   * ⚠️ **The cooking slots are numbered LAST and drawn FIRST** — top left, which
   * is where haugur put them. 🔴 **That is the rule this comment was already
   * making**: a slot number is an identity on the wire and never a position on
   * the glass, so the two new ones were appended rather than inserted at 0.
   */
  const HEARTH_WOOD = 2;
  const HEARTH_OUTPUT = 4;
  const HEARTH_COOK = 2;
  const HEARTH_SLOT_COUNT = HEARTH_WOOD + HEARTH_OUTPUT + HEARTH_COOK;

  /** What the open hearth says, in the player's own language. */
  interface HearthLabels extends PackLabels {
    /** 🔥 D135 — the pit-house oven: wood only, no pot and no shelf. */
    oven?: boolean;
    /** The hearth's own name. ⚠️ Icelandic — it is a thing, not a sentence. */
    readonly hearth?: string | undefined;
    readonly wood?: string | undefined;
    /** ⚫🍲 The output shelf. ⚠️ It holds cooked food AND charcoal since D121. */
    readonly charcoal?: string | undefined;
    /** 🍲 What is waiting to be cooked — D121. */
    readonly toCook?: string | undefined;
    /**
     * 🔥 What the fire is doing, as a finished sentence.
     *
     * 🔴 **Passed in, never worked out here.** How long a log lasts is
     * `hearth.ts`'s arithmetic and the words are `en.json`'s; this module knows
     * neither. `null` draws nothing, which is right for a fire that is out and
     * has nothing to say beyond its empty slots.
     */
    readonly fire?: string | null | undefined;
    /**
     * 🏠 Whether this is where you would wake, as a finished sentence — D108.
     *
     * 🔴 **Drawn on the fire's line, after it, never under it.** There are
     * 12 px between that baseline and the pack's rule, so a second line would
     * have to push the pack down — and the pack's clearance from the health rod
     * is the 2 px T170 measured. `null` draws nothing.
     */
    readonly home?: string | null | undefined;
    /** Whether the fire is lit, which decides the flame's colour only. */
    readonly lit?: boolean | undefined;
  }

  /**
   * 🔥 Where everything on the hearth screen sits.
   *
   * 🔴 **The same outer footprint as {@link packLayout}, on purpose** — 190 down
   * from the top, 556 across, 480 tall. ⚠️ **That is not tidiness, it is the
   * clearance test:** T170 measured **2 px** between the bottom of the pack and
   * the health rod at 1.75×, so a panel that grew even slightly taller would
   * land on the bars. Sharing the footprint means this screen inherits a number
   * that is already proven rather than needing its own.
   *
   * ## 🎯 haugur's own arrangement, 2026-09-22 — D121
   *
   * *"2 boxes on the loeft for what is to be coocked, 2 boxes below for the fuel
   * and 4 boxes on the right for the finished cooked produgt."*
   *
   * **So the left is a COLUMN of two rows and the right is still one row**, with
   * the flame between them — the row still reads left to right as *the fire
   * turns this into that*, and what changed is that the fire is now given two
   * things rather than one.
   *
   * ## 🔴 The panel grew UPWARD, and the bottom edge did not move
   *
   * ⚠️ **The clearance BELOW this screen is what the layout is built around.**
   * T170's finding — the pack coming within **2 px** of the health rod at 1.75×
   * — is why `design-hud-hearth.mjs` prints this panel's own clearance rather
   * than leaving it to the eye. **A row added at the bottom is the one change
   * that can put a panel on the bars.**
   *
   * ✅ **So `y` moved UP by exactly one row — `SLOT + GAP`, 52 — instead.**
   * 📏 **Measured both ways rather than reasoned about: the bottom sits at 620
   * before the change and 620 after, 52 px clear of the health rod in both.**
   * 🔴 **`ry` is what makes that work** — the pack below is placed from the TOP
   * of the fire block plus its height, so the block growing downward and the
   * panel growing upward cancel exactly.
   *
   * ⚠️ **It no longer shares the pack panel's `y` of 190**, and that is the one
   * thing this gives up. Sharing the footprint was about inheriting a number
   * somebody had proved, **and the number that was proved is at the bottom.**
   */
  function hearthLayout() {
    const cols = 8;
    const rows = 3;
    const PAD = 20;
    const w = BARW + PAD * 2;
    const x = Math.round((SW - w) / 2);
    /* 🔴 As far above 190 as the fire block grew — see the note above, and
       `fireH` below for what it grew BY. The bottom edge is unchanged, which is
       the edge the clearance was measured at. ⚠️ Written as the arithmetic
       rather than as a number, so the day the block changes height again the
       bottom still does not move. */
    const y = 190 - (SLOT + 20);
    const titleH = 46;

    /**
     * The fire, in three columns: **what is to be cooked · the flame with the
     * fuel under it · what comes out.**
     *
     * 🎯 **haugur moved the fuel there himself, 2026-09-22:** *"i think the 2
     * fuel boxes should be belowe the pic of the flame, i think it is more
     * understandable."* 🔴 **And it is: the fuel is what the FLAME eats, so it
     * belongs under the flame and not under the fish.** The row then reads
     * *this goes in · the fire, and what feeds it · this comes out.*
     * ⚠️ **They sat under the cooking slots for one baked picture**, which put
     * the wood in the same column as the food going in — two different jobs
     * stacked as if they were one.
     */
    const COLGAP = 28;
    const woodW = HEARTH_WOOD * SLOT + (HEARTH_WOOD - 1) * GAP;
    const cookW = HEARTH_COOK * SLOT + (HEARTH_COOK - 1) * GAP;
    /** The middle column is as wide as the fuel row it carries. */
    const FLAME_W = woodW;
    const charW = HEARTH_OUTPUT * SLOT + (HEARTH_OUTPUT - 1) * GAP;
    const fireW = cookW + COLGAP + FLAME_W + COLGAP + charW;
    const fx = x + PAD + Math.round((BARW - fireW) / 2);
    /**
     * 🍲 The cooking row is on TOP and 🪵 the fuel row below it — his order.
     *
     * 🔴 **`LABEL_GAP`, not `GAP`, and a baked picture is why.** The fuel row's
     * own label is drawn 9 px above it, and at a plain 4 px gap that lands
     * **inside the cooking boxes** — *"Wood — burns"* was printed across them,
     * with a fish's count showing through the word. ⚠️ **Every test passed and
     * the hit-testing was perfect**; it was visible the moment the sheet was
     * looked at. 👉 ***You cannot proofread a picture.***
     */
    const LABEL_GAP = 20;
    /** 🍲 The top row: what is to be cooked, the flame, and what comes out. */
    const ky = y + titleH + 10;
    /** 🪵 The fuel, on its own row under the flame. */
    const fy = ky + SLOT + LABEL_GAP;
    /** How tall the whole fire block is: two rows, with a label between them. */
    const fireH = SLOT * 2 + LABEL_GAP;
    const flameX = fx + cookW + COLGAP;
    const cx = flameX + FLAME_W + COLGAP;
    /* ⚫🍲 What comes out sits on the SAME line as what goes in, so the top row
       reads straight across as *this becomes that*. */
    const oy = ky;

    /* Then the player's own things, in the pack's own order and spacing. */
    const packH = rows * SLOT + (rows - 1) * GAP;
    const packW = cols * SLOT + (cols - 1) * GAP;
    const ry = ky + fireH + 30;
    const px = x + PAD + Math.round((BARW - packW) / 2);
    const py = ry + 24;
    const hry = py + packH + 16;
    const hx = x + PAD;
    const hy = hry + 24;
    const h = hy + SLOT + 12 + PAD - y;

    return {
      cols,
      rows,
      PAD,
      x,
      y,
      w,
      h,
      titleH,
      /** 🍲 The two cooking slots — the top row, left column. */
      fx,
      /** 🪵 The row the two fuel slots sit on, under the flame at `flameX`. */
      fy,
      /** The top row: cooking slots, flame and output all share it. */
      ky,
      /** How tall the fire block is: two rows. */
      fireH,
      /** The flame between the two groups. */
      flameX,
      FLAME_W,
      /** ⚫🍲 The four output slots, and the row they sit on. */
      cx,
      oy,
      packW,
      packH,
      ry,
      px,
      py,
      hry,
      hx,
      hy,
      SLOT,
      GAP,
    };
  }

  /**
   * 🔥 Which slot a click landed on, on the hearth screen.
   *
   * 🔴 **Two containers, so a bare number is not enough.** The pack's
   * {@link packSlotAt} answers with one integer because everything it can hit
   * belongs to the same player row. Here a click may land on the **hearth**,
   * which is a different table on the server, and the two number spaces would
   * otherwise collide at zero. ⚠️ **`06_Traps.md`: a slot number that means two
   * different places is the shape of a bug nobody sees** — it moves the right
   * count of the right item into the wrong box.
   *
   * `player` numbers match {@link packSlotAt} exactly: 0–9 on hand, 10–33 pack.
   */
  function hearthSlotAt(
    mx: number,
    my: number,
  ): { where: 'hearth' | 'player'; slot: number } | null {
    const L = hearthLayout();

    const inGrid = (gx: number, gy: number, cols: number, rows: number): number | null => {
      const col = Math.floor((mx - gx) / (SLOT + GAP));
      const row = Math.floor((my - gy) / (SLOT + GAP));
      if (col < 0 || row < 0 || col >= cols || row >= rows) return null;
      /* ⚠️ The gap between two slots belongs to NEITHER. */
      if (mx - gx - col * (SLOT + GAP) > SLOT) return null;
      if (my - gy - row * (SLOT + GAP) > SLOT) return null;
      return row * cols + col;
    };

    /* 🪵 The fuel is under the FLAME — `flameX`, not `fx`. 🎯 haugur's own
       arrangement, and the x it is drawn at must be the x a click is read at. */
    const inWood = inGrid(L.flameX, L.fy, HEARTH_WOOD, 1);
    if (inWood !== null) return { where: 'hearth', slot: inWood };

    const inChar = inGrid(L.cx, L.oy, HEARTH_OUTPUT, 1);
    if (inChar !== null) return { where: 'hearth', slot: HEARTH_WOOD + inChar };

    /* 🍲 Drawn first, numbered last — see the note on the constants. */
    const inCook = inGrid(L.fx, L.ky, HEARTH_COOK, 1);
    if (inCook !== null) {
      return { where: 'hearth', slot: HEARTH_WOOD + HEARTH_OUTPUT + inCook };
    }

    const inPack = inGrid(L.px, L.py, L.cols, L.rows);
    if (inPack !== null) return { where: 'player', slot: HAND_SLOTS + inPack };

    const inHand = inGrid(L.hx, L.hy, HAND_SLOTS, 1);
    return inHand === null ? null : { where: 'player', slot: inHand };
  }

  /** Where one slot is drawn, so the hover box and the carried item agree with the clicks. */
  function hearthSlotRect(
    where: 'hearth' | 'player',
    slot: number,
  ): { x: number; y: number } | null {
    const L = hearthLayout();
    if (where === 'hearth') {
      if (!Number.isInteger(slot) || slot < 0 || slot >= HEARTH_SLOT_COUNT) return null;
      if (slot < HEARTH_WOOD) return { x: L.flameX + slot * (SLOT + GAP), y: L.fy };
      if (slot < HEARTH_WOOD + HEARTH_OUTPUT) {
        return { x: L.cx + (slot - HEARTH_WOOD) * (SLOT + GAP), y: L.oy };
      }
      const i = slot - HEARTH_WOOD - HEARTH_OUTPUT;
      return { x: L.fx + i * (SLOT + GAP), y: L.ky };
    }
    if (!Number.isInteger(slot) || slot < 0 || slot >= HAND_SLOTS + L.cols * L.rows) return null;
    if (slot < HAND_SLOTS) return { x: L.hx + slot * (SLOT + GAP), y: L.hy };
    const i = slot - HAND_SLOTS;
    return {
      x: L.px + (i % L.cols) * (SLOT + GAP),
      y: L.py + Math.floor(i / L.cols) * (SLOT + GAP),
    };
  }

  /**
   * 📦 **The chest’s own slots — 27, in nine columns of three** (D106, T135).
   *
   * 🎯 **haugur, 2026-09-02:** *"27 for a single chest, double that for a
   * double chest?"* — which is Minecraft’s number, and
   * `Development/Minecraft/09` §4 says what was actually taken from it: container
   * capacity **NEVER GROWS** across that whole game. A bigger box solves the
   * storage problem, and solving it deletes the building.
   *
   * ⚠️ **Literals here and `CHEST_SLOTS` in `@lofi/shared`, agreed rather than
   * imported** — the same constraint that keeps `HAND_SLOTS` and the hearth’s two
   * numbers literals: the design generators resolve this module as a plain file
   * and may not reach for the workspace. `hud-scene.test.ts` checks they agree.
   *
   * ⚠️ **Nine wide, not eight like the pack.** Three rows of nine is 27 exactly;
   * eight would leave a ragged last row of three, and a grid with a gap in it
   * reads as slots that are missing rather than as slots that are empty.
   */
  const CHEST_COLS = 9;
  const CHEST_ROWS = 3;
  const CHEST_SLOTS = CHEST_COLS * CHEST_ROWS;

  /** What the open chest says, in the player’s own language. */
  interface ChestLabels extends PackLabels {
    /** The chest’s own name. ⚠️ Icelandic — it is a thing, not a sentence. */
    readonly chest?: string | undefined;
    /** 🗂️ The two tabs a container with a fire has — D121. */
    readonly tabFood?: string | undefined;
    readonly tabFire?: string | undefined;
    /**
     * 🍲 The fire's three group labels, drawn by `fireGroups`.
     *
     * ⚠️ **`charcoal` is the OUTPUT shelf's label and a búr's says something
     * different** — 🔴 a búr's fire makes no charcoal (D71), so calling its
     * shelf *"Cooked · charcoal"* would name a thing that can never appear
     * there. **A label describing what a screen cannot do is the fault this
     * repo keeps paying for.**
     */
    readonly toCook?: string | undefined;
    readonly wood?: string | undefined;
    readonly charcoal?: string | undefined;
  }

  /**
   * 📦 Where everything on the chest screen sits.
   *
   * 🔴 **It shares the pack’s BOTTOM edge, not its top**, and that is the whole
   * trick. {@link packLayout} and {@link hearthLayout} both start at 190 because
   * T170 measured **2 px** of clearance between the bottom of the pack and the
   * health rod at 1.75× — so the proven number is where the panel ENDS.
   *
   * ⚠️ **This screen draws 352 px of slots where the pack draws 248**: 27 chest
   * slots, then the player’s 24, then the hand row. It cannot be 480 tall and
   * hold them, so it starts higher and finishes in the same place. A test pins
   * the two bottoms together, because the number that must not drift is that one.
   *
   * ⚠️ **No clothing row and no bin**, exactly as the hearth: you do not dress
   * yourself at a chest, and a destructive target one click from a store you came
   * here to fill is a mis-click that costs a stack.
   */
  /**
   * @param capacity 27 for a chest, 54 for a storehouse door — protocol 23.
   *
   * 🏠 **Six rows do not fit in the chest's spacing.** 46 + 308 + 40 + 152 +
   * 40 + 48 + 12 + 20 is 666 and the panel's top would sit above the status
   * bar. So a container taller than three rows draws TIGHT — 30 px between
   * groups instead of 40, a 38 px title, 12 px padding: 630, with 40 px
   * under the bar. **The chest keeps its own, approved spacing.**
   */
  function chestLayout(
    capacity: number = CHEST_SLOTS,
    /** 🧵 D136 — a loom's three slots stand in a chest-tall space, for its picture. */
    rowsAtLeast = 1,
  ) {
    const cols = 8;
    const rows = 3;
    const gridRows = Math.max(rowsAtLeast, Math.ceil(capacity / CHEST_COLS));
    const tight = gridRows > CHEST_ROWS;
    const PAD = tight ? 12 : 20;
    const w = BARW + 20 * 2;
    const x = Math.round((SW - w) / 2);
    const titleH = tight ? 38 : 46;
    /* ⚠️ A group gap is the rule's offset PLUS the 24 to the row below it — 6 + 24
       tight, 16 + 24 loose. The first cut of this counted 16 and the hand row
       ran 4 px past the panel's bottom; sheet 74 caught it. */
    const between = tight ? 30 : 40;

    /* 📦 The chest’s own grid — nine across, and it fits: 9 × 48 + 8 × 4 is 464
       inside BARW’s 516. */
    const chestW = CHEST_COLS * SLOT + (CHEST_COLS - 1) * GAP;
    const chestH = gridRows * SLOT + (gridRows - 1) * GAP;
    const packW = cols * SLOT + (cols - 1) * GAP;
    const packH = rows * SLOT + (rows - 1) * GAP;

    /* 🔴 Measured UPWARD from the pack’s proven bottom edge. */
    const h = titleH + chestH + between + packH + between + SLOT + 12 + PAD;
    const y = PACK_BOTTOM - h;

    const gx = x + 20 + Math.round((BARW - chestW) / 2);
    const gy = y + titleH;
    const ry = gy + chestH + (tight ? 6 : 16);
    const px = x + 20 + Math.round((BARW - packW) / 2);
    const py = ry + 24;
    const hry = py + packH + (tight ? 6 : 16);
    const hx = x + 20;
    const hy = hry + 24;

    return {
      cols,
      rows,
      PAD,
      x,
      y,
      w,
      h,
      titleH,
      /** The chest’s 27. */
      gx,
      gy,
      chestW,
      chestH,
      packW,
      packH,
      ry,
      px,
      py,
      hry,
      hx,
      hy,
      SLOT,
      GAP,
      gridRows,
      capacity,
    };
  }

  /**
   * 📦 Which slot a click landed on, on the chest screen.
   *
   * 🔴 **Two containers, so a bare number is not enough** — {@link hearthSlotAt}
   * says why, and the reason is unchanged: both number spaces start at zero, and
   * one that means two different places moves the right count of the right item
   * into the wrong box with nothing raised anywhere.
   *
   * `player` numbers match {@link packSlotAt} exactly: 0–9 on hand, 10–33 pack.
   */
  function chestSlotAt(
    mx: number,
    my: number,
    capacity: number = CHEST_SLOTS,
    rowsAtLeast = 1,
  ): { where: 'chest' | 'player'; slot: number } | null {
    const L = chestLayout(capacity, rowsAtLeast);

    const inGrid = (gx: number, gy: number, cols: number, rows: number): number | null => {
      const col = Math.floor((mx - gx) / (SLOT + GAP));
      const row = Math.floor((my - gy) / (SLOT + GAP));
      if (col < 0 || row < 0 || col >= cols || row >= rows) return null;
      /* ⚠️ The gap between two slots belongs to NEITHER. */
      if (mx - gx - col * (SLOT + GAP) > SLOT) return null;
      if (my - gy - row * (SLOT + GAP) > SLOT) return null;
      return row * cols + col;
    };

    const inChest = inGrid(L.gx, L.gy, CHEST_COLS, L.gridRows);
    if (inChest !== null && inChest < capacity) return { where: 'chest', slot: inChest };

    const inPack = inGrid(L.px, L.py, L.cols, L.rows);
    if (inPack !== null) return { where: 'player', slot: HAND_SLOTS + inPack };

    const inHand = inGrid(L.hx, L.hy, HAND_SLOTS, 1);
    return inHand === null ? null : { where: 'player', slot: inHand };
  }

  /** Where one slot is drawn, so the hover box and the carried item agree with the clicks. */
  function chestSlotRect(
    where: 'chest' | 'player',
    slot: number,
    capacity: number = CHEST_SLOTS,
    rowsAtLeast = 1,
  ): { x: number; y: number } | null {
    const L = chestLayout(capacity, rowsAtLeast);
    if (where === 'chest') {
      if (!Number.isInteger(slot) || slot < 0 || slot >= capacity) return null;
      return {
        x: L.gx + (slot % CHEST_COLS) * (SLOT + GAP),
        y: L.gy + Math.floor(slot / CHEST_COLS) * (SLOT + GAP),
      };
    }
    if (!Number.isInteger(slot) || slot < 0 || slot >= HAND_SLOTS + L.cols * L.rows) return null;
    if (slot < HAND_SLOTS) return { x: L.hx + slot * (SLOT + GAP), y: L.hy };
    const i = slot - HAND_SLOTS;
    return {
      x: L.px + (i % L.cols) * (SLOT + GAP),
      y: L.py + Math.floor(i / L.cols) * (SLOT + GAP),
    };
  }

  /**
   * 📦 The chest, open. **The same language as the pack and the hearth** —
   * carved slots, doubled edges, no ornament — because it is the same kind of
   * screen, and a third dialect would only teach the player to look twice.
   */

  /**
   * 🥫🔥 **Where a container's fire sits on its own screen** — D121, T222b.
   *
   * 🎯 **haugur, 2026-09-22, shown that the búr's screen is already 630 px of a
   * 670 px budget and overlaps the health bars at a small window:** *"Tabs —
   * Food and Fire."*
   *
   * 🔴 **So the fire does not squeeze in beside the food; it replaces it.** The
   * Fire tab draws the same three groups the hearth does, **from the same
   * `fireGroups`**, at the top of the container's own panel.
   *
   * ⚠️ **Measured, not guessed.** The búr's panel bottom is at 670 with **2 px**
   * of clearance on haugur's screen and **−35 at 1280×800 — it already overlaps
   * the bars there**, which is a fault older than this change and is why
   * nothing may be added to that page.
   */
  function containerFireLayout(capacity: number) {
    const L = chestLayout(capacity);
    const COLGAP = 28;
    const woodW = HEARTH_WOOD * SLOT + (HEARTH_WOOD - 1) * GAP;
    const cookW = HEARTH_COOK * SLOT + (HEARTH_COOK - 1) * GAP;
    const charW = HEARTH_OUTPUT * SLOT + (HEARTH_OUTPUT - 1) * GAP;
    const fireW = cookW + COLGAP + woodW + COLGAP + charW;
    const fx = L.x + L.PAD + Math.round((L.w - L.PAD * 2 - fireW) / 2);
    /**
     * 🔴 **Below the title's line, not on it.**
     *
     * ⚠️ **A baked picture is why.** `fireGroups` draws each group's label 9 px
     * above its slots, and at `L.gy` that put *"To cook"* **straight through the
     * word Býtibúr** — the label simply was not there to read. **Every test
     * passed**: the slots answered their own clicks perfectly, with their name
     * invisible.
     */
    const ky = L.gy + 16;
    return {
      fx,
      ky,
      fy: ky + SLOT + 20,
      oy: ky,
      flameX: fx + cookW + COLGAP,
      FLAME_W: woodW,
      cx: fx + cookW + COLGAP + woodW + COLGAP,
    };
  }

  /**
   * 🥫🔥 Which fire slot a click lands on, on a container's Fire tab, or `null`.
   *
   * 🔴 **The numbering is the hearth's** — 0–1 fuel, 2–5 output, 6–7 to cook —
   * because it goes up the wire as a `move_hearth_item`, and **a slot number is
   * an identity on the wire, never a position on the glass.**
   */
  function containerFireSlotAt(mx: number, my: number, capacity: number): number | null {
    const o = containerFireLayout(capacity);
    const inGrid = (gx: number, gy: number, cols: number): number | null => {
      const col = Math.floor((mx - gx) / (SLOT + GAP));
      if (col < 0 || col >= cols) return null;
      if (my < gy || my > gy + SLOT) return null;
      /* ⚠️ The gap between two slots belongs to NEITHER. */
      if (mx - gx - col * (SLOT + GAP) > SLOT) return null;
      return col;
    };
    const fuel = inGrid(o.flameX, o.fy, HEARTH_WOOD);
    if (fuel !== null) return fuel;
    const out = inGrid(o.cx, o.oy, HEARTH_OUTPUT);
    if (out !== null) return HEARTH_WOOD + out;
    const cook = inGrid(o.fx, o.ky, HEARTH_COOK);
    if (cook !== null) return HEARTH_WOOD + HEARTH_OUTPUT + cook;
    return null;
  }

  /**
   * 🗂️ Which of a container's tabs the mouse is over, as an index, or `null`.
   *
   * 🔴 **The same row the craft page's tabs use**, at the container's own grid
   * line — one measurement, asked by the drawing and by the click, because two
   * copies of a layout is the fault this repo names oftener than any other.
   */
  function containerTabXs(capacity: number, labels: readonly string[]): number[] {
    const L = chestLayout(capacity);
    /* 🪧 Clear of the container's NAME, which shares this line. 120 px is the
       widest of `Kistill`, `Býtibúr` and `Skemma` at size 22 with room to
       spare — and the name is drawn first, so a longer one would collide.
       ⚠️ Worth a measurement the day a fourth container is named. */
    let at = L.x + L.PAD + 120;
    return labels.map((label) => {
      const here = at;
      at += craftTabW(label) + CRAFT_TAB_GAP;
      return here;
    });
  }

  function containerTabAt(
    mx: number,
    my: number,
    capacity: number,
    labels: readonly string[],
  ): number | null {
    /* 🔴 The title's own line — see the note in `chestPanel` on why these are
       not under it. One measurement, asked by the drawing and by the click. */
    const baseY = chestLayout(capacity).y + 30;
    if (my < baseY - 14 || my > baseY + 8) return null;
    const xs = containerTabXs(capacity, labels);
    for (const [i, x0] of xs.entries()) {
      const label = labels.at(i) ?? '';
      if (mx >= x0 && mx <= x0 + craftTabW(label)) return i;
    }
    return null;
  }

  /**
   * 🥫🍲 **The búr's own food that this fire can cook, under the fire** —
   * T222c, D121.
   *
   * 🎯 **haugur, the hour the búr's fire shipped:** *"fire tab works fine but
   * my fish are in the other tab, so if i want to cook them, i have to first
   * drag them to onhand switch to fire and drag them there."* 🔴 **Three drags
   * and a tab switch to cook food out of your own food store.**
   *
   * ✅ **It goes in the space that was already empty.** 📏 **Measured: 160 px
   * between the fire and the Pack — three rows of nine** — which is the dead
   * area this screen was shipped with and flagged on the day.
   *
   * ⚠️ **It shows only what can be COOKED**, not the whole store. **That is not
   * a filtered container view; it is a shelf of what you may put in** — and it
   * is labelled as one, because *"where did my berries go"* is a fair question
   * to ask of a screen that hides half a box.
   */
  const SHELF_ROWS = 2;
  function containerShelfLayout(capacity: number) {
    const L = chestLayout(capacity);
    const o = containerFireLayout(capacity);
    /**
     * Below the fire's two rows AND the sentence under them.
     *
     * 🔴 **50, not 30, and TWO rows rather than three — a baked picture is
     * why.** At 30 the shelf's label sat on the same line as *"Burning — about
     * four hours of wood left"* and the two ran into each other. 📏 **And it
     * could not simply be pushed down: three rows already reached the Pack's
     * rule exactly.** ✅ **Two rows is eighteen squares** against four fish in
     * the whole game — ample, and it buys the clearance.
     */
    const top = o.ky + SLOT * 2 + 20 + 50;
    const cols = CHEST_COLS;
    const gw = cols * SLOT + (cols - 1) * GAP;
    return { x: L.x + Math.round((L.w - gw) / 2), y: top, cols, rows: SHELF_ROWS };
  }

  /** 🥫 How many of the búr's food slots the shelf can show at once. */
  function containerShelfCapacity(): number {
    return CHEST_COLS * SHELF_ROWS;
  }

  /**
   * 🥫 Which shelf square the mouse is over, as an index into what was drawn —
   * **not a búr slot number.** The caller holds the mapping, because only it
   * knows which of the store's 54 it chose to put there.
   */
  function containerShelfAt(mx: number, my: number, capacity: number): number | null {
    const g = containerShelfLayout(capacity);
    const col = Math.floor((mx - g.x) / (SLOT + GAP));
    const row = Math.floor((my - g.y) / (SLOT + GAP));
    if (col < 0 || row < 0 || col >= g.cols || row >= g.rows) return null;
    /* ⚠️ The gap between two slots belongs to NEITHER. */
    if (mx - g.x - col * (SLOT + GAP) > SLOT) return null;
    if (my - g.y - row * (SLOT + GAP) > SLOT) return null;
    return row * g.cols + col;
  }

  /**
   * 🖱️ **Where the hover box goes, for any of the four places on this screen.**
   *
   * 🔴 **One function, because there is one question.** ⚠️ The shelf's `slot` is
   * a real container slot, so its POSITION has to be found by where it sits on
   * the shelf — which only the caller knows, and is why a shelf hover is drawn
   * from the index it was found at rather than from the slot number.
   */
  function hoverRect(
    hover: { where: 'chest' | 'player' | 'fire' | 'shelf'; slot: number },
    capacity: number,
    rowsAtLeast = 1,
  ): { x: number; y: number } | null {
    if (hover.where === 'fire') {
      const o = containerFireLayout(capacity);
      if (hover.slot < HEARTH_WOOD) {
        return { x: o.flameX + hover.slot * (SLOT + GAP), y: o.fy };
      }
      if (hover.slot < HEARTH_WOOD + HEARTH_OUTPUT) {
        return { x: o.cx + (hover.slot - HEARTH_WOOD) * (SLOT + GAP), y: o.oy };
      }
      const i = hover.slot - HEARTH_WOOD - HEARTH_OUTPUT;
      return { x: o.fx + i * (SLOT + GAP), y: o.ky };
    }
    if (hover.where === 'shelf') {
      /* ⚠️ Drawn from the SHELF INDEX the caller passes as `slot` here — see
         `containerShelfRect`. */
      return containerShelfRect(hover.slot, capacity);
    }
    return chestSlotRect(hover.where, hover.slot, capacity, rowsAtLeast);
  }

  /** 🥫 Where one shelf square is drawn, by its position on the shelf. */
  function containerShelfRect(index: number, capacity: number): { x: number; y: number } | null {
    const g = containerShelfLayout(capacity);
    if (!Number.isInteger(index) || index < 0 || index >= g.cols * g.rows) return null;
    return {
      x: g.x + (index % g.cols) * (SLOT + GAP),
      y: g.y + Math.floor(index / g.cols) * (SLOT + GAP),
    };
  }

  /** 🧵 How tall a loom's screen is drawn, in chest rows — room for its picture. */
  const LOOM_ROWS = 3;

  /**
   * 🧵 **The loom's half of its screen — D136.** Under the three slots their
   * names and what it is doing, with a bar for the step under way; to their
   * right a picture of the loom — 🎯 *"a picture of the loom with the cloth
   * growing"*, from the table he said yes to. A front view: two uprights, the
   * top beam, the heddle rod, the warp, the cloth woven down from the beam, and
   * twelve stones at the foot.
   */
  function loomGroup(gx: number, gy: number, right: number, loom: LoomPicture): string[] {
    const g: string[] = [];
    const names = [loom.warp, loom.weft, loom.cloth];
    names.forEach((n, i) => {
      g.push(
        txt(gx + i * (SLOT + GAP) + SLOT / 2, gy + SLOT + 15, n, {
          size: 11,
          fill: C.tolg,
          anchor: 'middle',
        }),
      );
    });
    const barW = 3 * SLOT + 2 * GAP;
    g.push(txt(gx, gy + SLOT + 42, loom.line, { size: 12, fill: C.kerti }));
    g.push(rect(gx, gy + SLOT + 52, barW, 5, C.tolg, { op: 0.22 }));
    const busy = loom.doing === 'warping' || loom.doing === 'weaving';
    if (busy) g.push(rect(gx, gy + SLOT + 52, barW * loom.part, 5, C.rodi, { op: 0.9 }));

    /* 🖼️ The picture, centred in what is left of the grid's space. */
    const left = gx + barW + 28;
    const H = LOOM_ROWS * SLOT + (LOOM_ROWS - 1) * GAP;
    const W = Math.min(right - left, 150);
    const px = left + Math.round((right - left - W) / 2);
    const wood = '#8a6a45';
    const top = gy + 10;
    const foot = gy + H - 4;
    const postW = 6;
    /* The uprights, the top beam, and the heddle rod across at mid height. */
    g.push(rect(px + 8, top, postW, foot - top, wood));
    g.push(rect(px + W - 8 - postW, top, postW, foot - top, wood));
    g.push(rect(px, top + 4, W, 7, wood, { rx: 3 }));
    const warpTop = top + 12;
    const warpFoot = foot - 30;
    const wx0 = px + 8 + postW + 6;
    const wx1 = px + W - 8 - postW - 6;
    if (loom.doing !== 'idle') {
      /* 🧵 The warp — appearing while it is hung. */
      const op = loom.doing === 'warping' ? 0.25 + 0.75 * loom.part : 1;
      for (let i = 0; i <= 24; i++) {
        const tx = wx0 + ((wx1 - wx0) * i) / 24;
        g.push(rect(tx, warpTop, 1, warpFoot - warpTop, '#cfc6b4', { op }));
      }
      /* 🧶 The cloth, woven down from the beam: whole ells, and the one under way. */
      const clothH = (warpFoot - warpTop) * loom.woven;
      if (clothH > 0) g.push(rect(wx0 - 2, warpTop, wx1 - wx0 + 4, clothH, '#b5aa95'));
      /* 🪨 Twelve kljásteinar, in two rows. */
      for (let i = 0; i < 12; i++) {
        const row = i % 2;
        const sx = wx0 + 4 + ((wx1 - wx0 - 8) * Math.floor(i / 2)) / 5 + row * 5;
        const sy = warpFoot + 8 + row * 9;
        g.push(`<circle cx="${sx}" cy="${sy}" r="5" fill="#3e3b38"/>`);
        g.push(`<circle cx="${sx}" cy="${sy}" r="1.6" fill="${C.nott}"/>`);
      }
    }
    g.push(rect(px, top + (warpFoot - top) * 0.45, W, 3, wood, { op: 0.9 }));
    return g;
  }

  function chestPanel(
    name: VariantName,
    /** 27. Short arrays draw empty, which is what an empty chest sends. */
    chest: readonly SlotContent[],
    pack: readonly SlotContent[],
    hand: readonly SlotContent[],
    selected: number,
    /** 🖱️ What the mouse is over, or `null`. Absent draws nothing at all. */
    hover: { where: 'chest' | 'player' | 'fire' | 'shelf'; slot: number } | null = null,
    labels: ChestLabels = {},
    /** 🏠 27 for a chest, 54 for a storehouse door. The screen grows to it. */
    capacity: number = CHEST_SLOTS,
    /**
     * 🥫🔥 **This container's fire, as eight slots** — D121, T222b.
     *
     * 🔴 **An empty array means NO FIRE**, which is every chest and every
     * skemma, and the screen then has no tabs at all — exactly the page it has
     * always been. **Only a búr has one.**
     */
    fire: readonly SlotContent[] = [],
    /** 🗂️ Which tab is open. Ignored when there is no fire. */
    tab: 'food' | 'fire' = 'food',
    /** 🔥 What the fire is doing, as a finished sentence, or `null`. */
    fireLine: string | null = null,
    /**
     * 🥫🍲 **The store's own food that this fire can cook** — T222c. Drawn
     * under the fire, in the space this screen already had spare.
     */
    shelf: readonly SlotContent[] = [],
    /** The shelf's own label, e.g. *"In the búr — drag up to cook"*. */
    shelfLabel: string | null = null,
    /**
     * 🧵 **A vefstofa's sentence, beside its two slots** — D127. `null` for
     * every other container. ⚠️ Beside the grid rather than under it: a
     * two-slot grid leaves the whole row empty to its right.
     */
    spinLine: string | null = null,
    /**
     * 🧵 **A loom's screen — D136.** `null` for every other container. Its
     * three slots, their names under them, what it is doing and how far, and a
     * picture of the loom with the cloth growing down from the beam.
     */
    loom: LoomPicture | null = null,
  ): string {
    // eslint-disable-next-line security/detect-object-injection -- `name` is a VariantName, a literal union.
    const k = VARIANTS[name].rod === 'tally' ? 'tally' : 'carved';
    const g: string[] = [];
    const L = chestLayout(capacity, loom === null ? 1 : LOOM_ROWS);
    const { cols, rows, PAD, x, y, w, h } = L;

    g.push(rect(0, TOPBAR, SW, SH - TOPBAR, C.nott, { op: 0.58 }));
    g.push(keyed(x, y, w, h));
    g.push(rect(x, y, w, h, C.nott, { op: 0.95 }));
    g.push(rect(x, y, w, 1, C.kerti, { op: 0.14 }));

    /* ⚠️ A TITLE, not a tab — the hearth’s rule and its reason: this screen has
       one page and belongs to one block you had to walk to. */
    g.push(
      txt(x + PAD, y + 30, labels.chest ?? 'Kistill', {
        size: 22,
        font: SERIF,
        weight: 500,
        fill: C.kerti,
      }),
    );
    g.push(
      txt(x + w - PAD, y + 29, labels.close ?? 'E  or  Esc  to close', {
        size: 11,
        fill: C.tolg,
        anchor: 'end',
      }),
    );

    /**
     * 🗂️ **Food · Fire** — D121, and only when this container HAS a fire.
     *
     * 🎯 **haugur chose tabs over one long screen**, shown that the búr's page
     * is already 630 px of a 670 px budget. ⚠️ **Drawn in the same style as the
     * craft page's tabs (D122), because it is the same idea** — one thing to
     * learn rather than two.
     */
    const hasFire = fire.length > 0;
    if (hasFire) {
      const words = [labels.tabFood ?? 'Food', labels.tabFire ?? 'Fire'];
      /**
       * 🔴 **BESIDE the title, not under it.** ⚠️ **A baked picture is why**:
       * this screen's `titleH` is 38 and its title sits on `y + 30`, **so there
       * are EIGHT pixels under it** — and the first version drew *Food · Fire*
       * straight through the word *Býtibúr*. 🔴 **Every test passed**: the tabs
       * answered their own clicks perfectly, in the wrong place.
       * 👉 ***You cannot proofread a picture.***
       *
       * ⚠️ **The CONTAINER's own left edge, not the pack's** — `craftTabXs`
       * measures from `packLayout` and the two panels are different widths.
       * **The drawing and the hit test agreed with each other and were both
       * wrong**, which only a test against this layout could see.
       */
      const xs = containerTabXs(capacity, words);
      for (const [i, x0] of xs.entries()) {
        const word = words.at(i) ?? '';
        const lit = (i === 1) === (tab === 'fire');
        g.push(
          txt(x0, L.y + 30, word, {
            size: 14,
            font: SERIF,
            fill: lit ? C.kerti : C.tolg,
          }),
        );
        if (lit) g.push(rect(x0, L.y + 38, craftTabW(word), 2, C.lampi, { op: 0.85 }));
      }
    }

    /* 🔥 The fire's own page. 🔴 It REPLACES the food grid rather than sitting
       beside it — there is no room, and that is measured rather than felt. */
    if (hasFire && tab === 'fire') {
      /* 🔥 Drawn where the grid would have been, and the grid is not drawn. */
      g.push(fireGroups(k, containerFireLayout(capacity), fire, labels));
      if (fireLine !== null && fireLine !== '') {
        const o = containerFireLayout(capacity);
        g.push(
          txt(x + w / 2, o.ky + SLOT * 2 + 20 + 18, fireLine, {
            size: 11,
            fill: C.rodi,
            anchor: 'middle',
          }),
        );
      }

      /* 🥫🍲 The shelf — T222c. What is in the búr that this fire can cook,
         where you can reach it without leaving the tab. */
      if (shelf.length > 0) {
        const sh = containerShelfLayout(capacity);
        if (shelfLabel !== null && shelfLabel !== '') {
          g.push(txt(sh.x, sh.y - 9, shelfLabel, { size: 11, fill: C.tolg }));
        }
        for (let r = 0; r < sh.rows; r++)
          for (let c = 0; c < sh.cols; c++) {
            const i = r * sh.cols + c;
            /* ⚠️ `at` rather than `shelf[i]` — indexing an array with a
               variable is the object-injection pattern this file answers
               rather than suppresses, the same way `blocks.ts` keeps a Map. */
            g.push(P.slot(k, sh.x + c * (SLOT + GAP), sh.y + r * (SLOT + GAP), shelf.at(i) ?? {}));
          }
      }
    }

    /* 📦 The chest’s 27. ⚠️ NO label above them: the title already says what
       this box is, and a second word over the grid would be the screen
       explaining itself twice. The hearth needs its labels because its two
       groups mean different things; these 27 all mean the same thing. */
    /* 🔴 **NOT DRAWN ON THE FIRE TAB.** D121: the fire REPLACES the grid rather
       than sitting beside it — there is no room, and that is measured: the
       búr's panel is already 510 px of the 510 it has, and at 1280×800 it
       overlaps the health bars. ⚠️ **The first version drew both**, and the
       baked picture showed the fire squeezed invisibly behind 54 food slots. */
    for (let r = 0; !(hasFire && tab === 'fire') && r < L.gridRows; r++)
      for (let c = 0; c < CHEST_COLS; c++) {
        if (r * CHEST_COLS + c >= capacity) break;
        g.push(
          P.slot(
            k,
            L.gx + c * (SLOT + GAP),
            L.gy + r * (SLOT + GAP),
            chest[r * CHEST_COLS + c] ?? {},
          ),
        );
      }

    /* 🧵 D127 — an arrow from wool to thread, and what the house is doing. */
    if (spinLine !== null && spinLine !== '' && capacity < CHEST_COLS) {
      const mid = L.gy + SLOT / 2 + 4;
      g.push(txt(L.gx + SLOT + GAP / 2, mid, '→', { size: 11, fill: C.rodi, anchor: 'middle' }));
      g.push(txt(L.gx + capacity * (SLOT + GAP) + 10, mid, spinLine, { size: 11, fill: C.rodi }));
    }

    /* 🧵 D136 — the loom's names, its sentence, its bar and its picture. */
    if (loom !== null)
      g.push(...loomGroup(L.gx, L.gy, L.gx + CHEST_COLS * (SLOT + GAP) - GAP, loom));

    /* 🎒 The player’s own things, so a stack can be dragged between the two. */
    g.push(rect(x + PAD, L.ry, BARW, 1, 'rgba(242,233,218,0.14)'));
    g.push(txt(x + PAD, L.ry + 16, labels.title ?? 'Pack', { size: 11, fill: C.tolg }));
    for (let r = 0; r < rows; r++)
      for (let c = 0; c < cols; c++)
        g.push(
          P.slot(k, L.px + c * (SLOT + GAP), L.py + r * (SLOT + GAP), pack[r * cols + c] ?? {}),
        );

    g.push(rect(x + PAD, L.hry, BARW, 1, 'rgba(242,233,218,0.14)'));
    g.push(txt(x + PAD, L.hry + 16, labels.onHand ?? 'On hand — 10', { size: 11, fill: C.tolg }));
    g.push(hotbar(k, L.hx, L.hy, hand, selected));

    if (hover !== null) {
      /**
       * 🔴 **`capacity` MUST be passed.** 🎯 haugur, 2026-09-08: *"in skemma
       * menu, the highlight is 2.5 tiles below mouse, on the bottom menu its
       * 0.5 tiles below."*
       *
       * ⚠️ **It defaulted to 27 here while the grid was drawn with 54**, so the
       * highlight was positioned by a layout for a different container.
       *
       * 🔴 **The two numbers are the proof, and they come from `chestLayout`
       * measuring UPWARD from `PACK_BOTTOM`.** A shorter panel starts lower, so
       * the error is largest at the TOP of the screen and almost nothing at the
       * bottom — 2.5 tiles in the chest grid, 0.5 on the hand row. **A constant
       * offset would have meant something else entirely.**
       */
      /**
       * 🥫🔥 **AND THE FIRE AND THE SHELF HAVE THEIR OWN PLACES** — #734.
       *
       * 🎯 **haugur, 2026-09-22:** *"See how the white box is 3/4 below the box
       * it is supose to higlight"*, and then *"we have had this bug before with
       * other menus but you fixed it."* 🔴 **He is right — the note directly
       * above is the same symptom on the same screen family, with a different
       * cause.**
       *
       * ⚠️ **The earlier one was a wrong default argument. This one was a
       * second CODE PATH**: the click learned about the fire and the shelf, and
       * the hover went on asking the chest's grid — so the box was drawn
       * wherever that grid's slot happened to be. ✅ **Both now ask one
       * function**, and this is the drawing half of the same repair.
       */
      const at = hoverRect(hover, capacity, loom === null ? 1 : LOOM_ROWS);
      if (at !== null)
        g.push(
          rect(at.x - 2, at.y - 2, SLOT + 4, SLOT + 4, 'none', {
            stroke: C.kerti,
            sw: 1.5,
            op: 0.55,
            rx: 2,
          }),
        );
    }

    return g.join('');
  }
  /**
   * 🔥 The hearth, open. **The same language as the pack** — carved slots,
   * doubled edges, no ornament — because it is the same kind of screen.
   */

  /**
   * 🔥 **The fire's three groups, drawn** — what goes in, the flame with its
   * fuel under it, and what comes out.
   *
   * 🔴 **EXTRACTED FROM `hearthPanel` FOR D121's BÚR FIRE**, so that the hearth
   * screen and the búr's Fire tab draw **the same thing from the same code**.
   * ⚠️ **A second copy would be the fault this repo names oftener than any
   * other** — and here it would be worse than usual: the two would drift into
   * showing different slot ORDERS for one shared `slots` array, which puts the
   * right item in the wrong box with nothing raised anywhere.
   *
   * ✅ **The extraction is output-identical by construction**, and
   * `hud-scene.test.ts`'s frozen hash is what proves it: **the hearth screen is
   * in the frozen set, so a single pixel of difference fails the build.**
   *
   * @param o where the groups sit — the same field names `hearthLayout`
   *   produces, so the hearth hands its own layout straight in.
   */
  function fireGroups(
    k: 'carved' | 'tally',
    o: {
      fx: number;
      ky: number;
      fy: number;
      oy: number;
      flameX: number;
      FLAME_W: number;
      cx: number;
    },
    slots: readonly SlotContent[],
    labels: {
      toCook?: string | undefined;
      wood?: string | undefined;
      charcoal?: string | undefined;
      oven?: boolean | undefined;
    },
  ): string {
    const g: string[] = [];
    /* 🔥 D135 — the oven cooks nothing and keeps nothing: wood and flame only. */
    const oven = labels.oven === true;
    /* 🍲 What is waiting to be cooked — the top row, left column. D121. */
    if (!oven) g.push(txt(o.fx, o.ky - 9, labels.toCook ?? 'To cook', { size: 11, fill: C.tolg }));
    for (let i = 0; i < (oven ? 0 : HEARTH_COOK); i++)
      g.push(
        P.slot(k, o.fx + i * (SLOT + GAP), o.ky, slots[HEARTH_WOOD + HEARTH_OUTPUT + i] ?? {}),
      );

    /**
     * 🔥 The flame, between what goes in and what comes out — the whole of what
     * says these groups are one process.
     *
     * 🎯 **haugur asked for it BIGGER, 2026-09-22**, looking at the first baked
     * sheet: at 22 px in a 48 px row it read as a small mark between two groups
     * rather than as the fire itself. 📏 **44 px — it fills its row, with 2 px
     * of air top and bottom.** ⚠️ **It cannot simply grow further**: the *"Wood
     * — burns"* label's glyphs start about 10 px under this row, and the last
     * baked picture had a label sitting inside a box.
     *
     * ⚠️ Its COLOUR is the only thing that changes when the fire goes out, and
     * colour is never the only signal (V7/V28): the fuel slots being empty is
     * the other, and it is the one that caused it.
     */
    const FLAME = 44;
    g.push(P.icFlame(o.flameX + (o.FLAME_W - FLAME) / 2, o.ky + (SLOT - FLAME) / 2, FLAME, C.rodi));

    /* 🪵 What burns — under the flame it feeds. 🎯 haugur put it there:
     *"i think the 2 fuel boxes should be belowe the pic of the flame."* */
    g.push(txt(o.flameX, o.fy - 9, labels.wood ?? 'Wood — burns', { size: 11, fill: C.tolg }));
    for (let i = 0; i < HEARTH_WOOD; i++)
      // eslint-disable-next-line security/detect-object-injection -- `i` is this loop's own counter, bounded by HEARTH_WOOD.
      g.push(P.slot(k, o.flameX + i * (SLOT + GAP), o.fy, slots[i] ?? {}));

    /* ⚫🍲 What the fire leaves — cooked food AND charcoal share these four. */
    if (!oven)
      g.push(
        txt(o.cx, o.oy - 9, labels.charcoal ?? 'Cooked  ·  charcoal', { size: 11, fill: C.tolg }),
      );
    for (let i = 0; i < (oven ? 0 : HEARTH_OUTPUT); i++)
      g.push(P.slot(k, o.cx + i * (SLOT + GAP), o.oy, slots[HEARTH_WOOD + i] ?? {}));
    return g.join('');
  }

  function hearthPanel(
    name: VariantName,
    /** Eight: fuel 0–1, output 2–5, to-cook 6–7. Short arrays draw empty. */
    hearth: readonly SlotContent[],
    pack: readonly SlotContent[],
    hand: readonly SlotContent[],
    selected: number,
    /** 🖱️ What the mouse is over, or `null`. Absent draws nothing at all. */
    hover: { where: 'hearth' | 'player'; slot: number } | null = null,
    labels: HearthLabels = {},
  ): string {
    // eslint-disable-next-line security/detect-object-injection -- `name` is a VariantName, a literal union.
    const k = VARIANTS[name].rod === 'tally' ? 'tally' : 'carved';
    const g: string[] = [];
    const L = hearthLayout();
    const { cols, rows, PAD, x, y, w, h } = L;

    g.push(rect(0, TOPBAR, SW, SH - TOPBAR, C.nott, { op: 0.58 }));
    g.push(keyed(x, y, w, h));
    g.push(rect(x, y, w, h, C.nott, { op: 0.95 }));
    g.push(rect(x, y, w, 1, C.kerti, { op: 0.14 }));

    /* ⚠️ A TITLE, not a tab. The pack's title became a tab because it gained a
       second page; this screen has one page and belongs to one block. */
    g.push(
      txt(x + PAD, y + 30, labels.hearth ?? 'Eldstæði', {
        size: 22,
        font: SERIF,
        weight: 500,
        fill: C.kerti,
      }),
    );
    g.push(
      txt(x + w - PAD, y + 29, labels.close ?? 'E  or  Esc  to close', {
        size: 11,
        fill: C.tolg,
        anchor: 'end',
      }),
    );

    g.push(fireGroups(k, L, hearth, labels));

    /* 🔥 What it is doing, and 🏠 whether it is home, in words under the row
       they describe. ⚠️ ONE line, the fire's sentence first — see the note on
       `home` in HearthLabels for why there is no room for a second. Firelight
       while it burns; the label grey when the only thing to say is about home. */
    const said = [labels.fire, labels.home].filter(
      (s): s is string => s !== null && s !== undefined && s !== '',
    );
    if (said.length > 0)
      g.push(
        txt(x + w / 2, L.ky + L.fireH + 18, said.join('  ·  '), {
          size: 11,
          fill: labels.fire ? C.rodi : C.tolg,
          anchor: 'middle',
        }),
      );

    /* 🎒 The player's own things, so a stack can be dragged between the two.
       🎯 haugur asked for exactly this: *"i would also want to be able to move
       wood and chacoal from my onhand inventory with the mouse."* */
    g.push(rect(x + PAD, L.ry, BARW, 1, 'rgba(242,233,218,0.14)'));
    g.push(txt(x + PAD, L.ry + 16, labels.title ?? 'Pack', { size: 11, fill: C.tolg }));
    for (let r = 0; r < rows; r++)
      for (let c = 0; c < cols; c++)
        g.push(
          P.slot(k, L.px + c * (SLOT + GAP), L.py + r * (SLOT + GAP), pack[r * cols + c] ?? {}),
        );

    g.push(rect(x + PAD, L.hry, BARW, 1, 'rgba(242,233,218,0.14)'));
    g.push(txt(x + PAD, L.hry + 16, labels.onHand ?? 'On hand — 10', { size: 11, fill: C.tolg }));
    g.push(hotbar(k, L.hx, L.hy, hand, selected));

    /* ⚠️ NO clothing row and NO bin, and both are deliberate. You do not dress
       yourself at a fire, and the bin is a destructive target that has no reason
       to be one click from a store you came here to fill. Fewer things on a
       screen you opened for one job. */

    if (hover !== null) {
      const at = hearthSlotRect(hover.where, hover.slot);
      if (at !== null)
        g.push(
          rect(at.x - 2, at.y - 2, SLOT + 4, SLOT + 4, 'none', {
            stroke: C.kerti,
            sw: 1.5,
            op: 0.55,
            rx: 2,
          }),
        );
    }

    return g.join('');
  }

  return {
    P,
    C,
    SERIF,
    SANS,
    SW,
    SH,
    SLOT,
    GAP,
    BARW,
    X0,
    BAR_H,
    EXPO_W,
    EXPO_X,
    EXPO_Y,
    FOOD_Y,
    HEALTH_Y,
    CONTROLS_Y,
    HINT_Y,
    AIM_Y,
    HOTBAR_Y,
    SCRIM_H,
    VARIANTS,
    hintLine,
    aimLine,
    cluster,
    packPanel,
    craftPage,
    tabAt,
    craftRowAt,
    craftTabAt,
    craftCueAt,
    craftCueY,
    containerFireSlotAt,
    containerTabAt,
    containerShelfAt,
    containerShelfCapacity,
    hoverRect,
    /* 🔴 Exported so a TEST can ask where the rows begin rather than repeating
       the number. It was written as a literal `L.y + 78` in two tests, and when
       D122's tab row moved this down they went red having asserted the old
       geometry perfectly — *a second copy of a layout*, in the one place that
       is supposed to catch them. */
    craftTop,
    CRAFT_ROW_H,
    /* 🎡 Exported so the wheel clamps with the SAME arithmetic the panel draws
       with. 🔴 A second opinion about how many rows fit is exactly the fault
       `chestSlotAt`/`chestSlotRect` are kept in step to avoid. */
    craftRowsFit,
    craftMaxScroll,
    packLayout,
    packSlotAt,
    /* 🔥 The hearth, T128. */
    HEARTH_WOOD,
    HEARTH_OUTPUT,
    HEARTH_COOK,
    HEARTH_SLOT_COUNT,
    hearthLayout,
    hearthPanel,
    hearthSlotAt,
    hearthSlotRect,
    /* 📦 The chest, T135. */
    CHEST_COLS,
    CHEST_ROWS,
    CHEST_SLOTS,
    chestLayout,
    chestPanel,
    chestSlotAt,
    chestSlotRect,
    packSlotRect,
    packBinAt,
    CLOTHING,
  };
}
